(() => {
  'use strict';

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  };
  const delay = (callback, wait = 300) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };
  const cartAjaxUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) throw new Error(window.cartStrings?.error || 'Cart request failed');
    const match = value.match(/^([^?#]*)(.*)$/);
    const path = match?.[1] || value;
    const suffix = match?.[2] || '';
    return /\.(?:js|json)$/i.test(path) ? value : `${path}.js${suffix}`;
  };
  // Bound both headers and body reads. Never automatically retry a cart POST.
  const fetchCartResponse = async (url, init = {}) => {
    const controller = new AbortController();
    const started = Date.now();
    const endpoint = String(url).split('?')[0];
    const method = init.method || 'GET';
    const log = (stage, data = {}) => window.CartDebug?.log(stage, { endpoint, method, elapsedMs: Date.now() - started, ...data });
    const timer = window.setTimeout(() => controller.abort(), 15000);
    log('cart-http.start');
    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      log('cart-http.response', { status: response.status });
      const body = await response.text();
      log('cart-http.complete', { status: response.status });
      return { ok: response.ok, status: response.status, headers: response.headers,
        text: async () => body, json: async () => JSON.parse(body) };
    } catch (error) {
      const code = controller.signal.aborted ? 'TIMEOUT' : 'NETWORK_ERROR';
      log('cart-http.error', { code });
      throw Object.assign(new Error('Unable to verify your cart update. Please check your cart before trying again.'), { code });
    } finally { window.clearTimeout(timer); }
  };
  const postJson = (url, body) => fetchCartResponse(cartAjaxUrl(url), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const parseJsonResponse = async (response, fallbackMessage) => {
    const text = await response.text();
    try { return JSON.parse(text); }
    catch (_error) { throw new Error(fallbackMessage || window.cartStrings?.error || 'Cart request failed'); }
  };
  const cartErrorMessage = () => window.cartStrings?.error || 'Unable to update your cart. Please check your cart before trying again.';
  const cartError = (message = cartErrorMessage()) => Object.assign(new Error(message), { userMessage: message });
  // A failed POST can still have reached Shopify. Never retry a mutation here.
  const requestJson = async (url, init = {}, debug = {}) => {
    const startedAt = Date.now();
    const endpoint = String(url).split('?')[0];
    const log = (stage, data = {}) => window.CartDebug?.log(stage, { traceId: debug.traceId, endpoint, method: init.method || 'GET', ...data });
    log('http.start');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { ...init, headers: { ...init.headers, Accept: 'application/json' }, signal: controller.signal });
      log('http.response', { status: response.status, contentType: response.headers.get('content-type'), elapsedMs: Date.now() - startedAt });
      // Shopify's .js endpoints can label JSON as JavaScript. Parse as JSON
      // only (never execute it), while still rejecting HTML/challenge pages.
      if (!/^(?:application\/(?:[\w.+-]*\+)?json|(?:application|text)\/(?:x-)?javascript)(?:\s*;|$)/i.test(response.headers.get('content-type') || '')) throw cartError();
      let data;
      try { data = await response.json(); } catch (_error) { throw cartError(); }
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw cartError();
      if (!response.ok || data.errors || Number(data.status) >= 400) {
        const message = typeof data.description === 'string' ? data.description
          : typeof data.errors === 'string' ? data.errors : cartErrorMessage();
        throw Object.assign(cartError(message), { responseData: data, responseStatus: response.status });
      }
      return data;
    } catch (error) {
      const code = controller.signal.aborted ? 'TIMEOUT' : error.responseStatus ? `HTTP_${error.responseStatus}` : error.userMessage ? 'INVALID_RESPONSE' : 'NETWORK_ERROR';
      log('http.error', { code, name: error.name, elapsedMs: Date.now() - startedAt });
      throw Object.assign(error.userMessage ? error : cartError(), { code });
    } finally {
      window.clearTimeout(timeout);
    }
  };
  window.GeneralStoreRuntime = { ...window.GeneralStoreRuntime, requestJson };
  let cartChangeQueue = Promise.resolve();
  let cartChangeEpoch = 0;
  // Only change/update responses contain a complete cart; add responses do not.
  const isFullCart = (state) => Array.isArray(state?.items)
    && Number.isFinite(state.item_count) && Number.isFinite(state.total_price);

  const itemProperty = (item, name) => {
    const value = item?.properties?.[name];
    return value == null ? '' : String(value);
  };
  const syncVariantSelects = (root, variant) => {
    if (!root || !variant) return;
    root.querySelectorAll('select').forEach((select, index) => {
      if (variant.options?.[index] != null) select.value = String(variant.options[index]);
    });
  };
  const resolveSelectableVariant = (root, variants, options = {}) => {
    const selected = findVariant(variants, selectedValues(root));
    if (selected && (selected.available || !options.skipUnavailable)) return selected;
    if (!selected && !options.skipNonExistent) return null;
    const fallback = variants.find((variant) => variant.available);
    if (fallback) syncVariantSelects(root, fallback);
    return fallback || null;
  };
  const parseJsonScript = (root) => {
    try {
      return JSON.parse(
        root.querySelector('[data-product-variants], script[type="application/json"]')?.textContent || '[]'
      );
    } catch (_error) {
      return [];
    }
  };
  const formatMoney = (cents, format = '${{amount}}') => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    const numeric = (Number(cents) || 0) / 100;
    if (String(format).includes('{{')) {
      return String(format)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(numeric).toLocaleString('en-US'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, numeric.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
        .replace(/\{\{\s*amount\s*\}\}/g, numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: document.querySelector('html')?.dataset.currency || 'USD',
    }).format(numeric);
  };
  const selectedValues = (root) => Array.from(root.querySelectorAll('select')).map((select) => select.value);
  const findVariant = (variants, values) => variants.find((variant) =>
    values.every((value, index) => String(variant.options?.[index]) === String(value))
  );
  const dispatch = (target, name, detail = {}) => target.dispatchEvent(new CustomEvent(name, {
    bubbles: true,
    detail,
  }));

  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (!this.input) return;
      this.input.form?.addEventListener('reset', (event) => this.onReset(event));
      this.input.addEventListener('input', delay(() => this.onChange(), 250));
      this.onChange();
    }

    onChange() {
      this.resetButton?.classList.toggle('hidden', !this.input.value.length);
    }

    shouldResetForm() {
      return !document.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      event.preventDefault();
      if (!this.shouldResetForm()) return;
      this.input.value = '';
      this.onChange();
      this.input.focus();
    }

    onReset(event) {
      this.onFormReset(event);
    }
  }
  // main-search.js is a separate deferred asset and extends this constructor.
  // Keep the shared base explicit instead of relying on this bundle's closure.
  window.SearchForm = SearchForm;
  define('search-form', SearchForm);

  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      if (!this.input) return;
      this.results = this.querySelector('[data-predictive-search]');
      this.status = this.querySelector('.predictive-search-status');
      this.cache = new Map();
      this.controller = null;
      this.input.addEventListener('focus', () => this.input.value.trim() && this.search());
      this.input.addEventListener('input', delay(() => this.search(), 250));
      this.addEventListener('focusout', () => window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) return this.close(true);
      if (!this.results || !window.routes?.predictive_search_url) return;
      this.setAttribute('loading', '');
      if (this.status) this.status.textContent = this.dataset.loadingText || 'Loading';
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) return this.render(this.cache.get(key));
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        const markup = section?.innerHTML || '';
        this.cache.set(key, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.removeAttribute('loading');
      this.setAttribute('results', 'true');
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      const liveText = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
      if (this.status && liveText) this.status.textContent = liveText;
    }

    close(clear = false) {
      if (clear && this.input) this.input.value = '';
      this.removeAttribute('open');
      this.removeAttribute('loading');
      this.input?.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') return this.close();
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
      const options = Array.from(this.querySelectorAll('[role="option"], .predictive-search__item')).filter((item) => !item.hidden);
      if (!options.length) return;
      const active = options.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      if (event.key === 'Enter') {
        const link = options[active]?.querySelector('a') || options[active];
        if (active >= 0 && link) {
          event.preventDefault();
          link.click();
        }
        return;
      }
      event.preventDefault();
      const next = event.key === 'ArrowDown'
        ? (active + 1) % options.length
        : (active <= 0 ? options.length - 1 : active - 1);
      options.forEach((item, index) => item.setAttribute('aria-selected', String(index === next)));
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  define('predictive-search', PredictiveSearch);

  class CartRemoveButton extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items, cart-drawer-items')?.updateQuantity(this.dataset.index, 0, event);
      });
    }
  }
  define('cart-remove-button', CartRemoveButton);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.onQuantityChange) return;
      this.onQuantityChange = (event) => {
        const input = event.target.closest('input[name="updates[]"], input[data-index]');
        if (!input) return;
        window.clearTimeout(this.changeTimer);
        this.changeTimer = window.setTimeout(() => {
          if (input.isConnected) this.updateQuantity(input.dataset.index, Number(input.value), event);
        }, 300);
      };
      this.addEventListener('change', this.onQuantityChange);
      this.onQuantityKeydown = (event) => {
        if (event.key !== 'Enter') return;
        const input = event.target.closest('input[name="updates[]"], input[data-index]');
        if (!input) return;
        event.preventDefault();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.addEventListener('keydown', this.onQuantityKeydown);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Our own changes render every mounted cart surface below. Other
          // writers (PDP, quick add, apps) need a fresh read, never another POST.
          if (event?.source === 'cart-items') return;
          const owner = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
          if (owner !== this) return;
          const task = () => {
            const current = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
            return current?.recoverCart(current.getSectionsToRender());
          };
          const drawer = document.querySelector('cart-drawer');
          const job = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : cartChangeQueue.then(task);
          cartChangeQueue = job.catch(() => {});
          return job;
        });
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.changeTimer);
      this.removeEventListener('change', this.onQuantityChange);
      this.removeEventListener('keydown', this.onQuantityKeydown);
      this.onQuantityChange = null;
      this.onQuantityKeydown = null;
      this.cartUpdateUnsubscribe?.();
      this.cartUpdateUnsubscribe = null;
    }

    getSectionsToRender() {
      // One write updates all mounted surfaces, not just the one clicked.
      // At most five sections (Shopify bundled-section limit).
      return [
        ...(document.getElementById('CartDrawer') ? [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }] : []),
        { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ...(document.getElementById('cart-live-region-text') ? [{ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' }] : []),
        { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
      ].filter((item) => item.section);
    }

    findQuantityInput(key, line) {
      return Array.from(this.querySelectorAll('input[name="updates[]"][data-index]')).find((input) =>
        key ? input.dataset.cartItemKey === key : input.dataset.index === String(line)
      );
    }

    validQuantity(input, quantity) {
      const minimum = Number(input?.min || 0);
      const maximum = input?.max ? Number(input.max) : Infinity;
      const step = Number(input?.step || 1);
      return Number.isSafeInteger(quantity) && quantity >= 0 && (quantity === 0 || (
        quantity >= minimum && quantity <= maximum && step > 0 && (quantity - minimum) % step === 0
      ));
    }

    updateQuantity(line, quantity, event) {
      const input = this.findQuantityInput(null, line);
      const key = input?.dataset.cartItemKey;
      const amount = Number(quantity);
      if (!key || !this.validQuantity(input, amount)) {
        if (input) input.value = input.getAttribute('value');
        this.setLiveMessage(line, cartErrorMessage());
        return Promise.resolve();
      }
      const epoch = cartChangeEpoch;
      // Capture identity before rendering can renumber rows or replace this element.
      const task = () => {
        if (epoch !== cartChangeEpoch) return;
        const owner = this.isConnected ? this : document.querySelector(this.tagName.toLowerCase());
        const currentInput = owner?.findQuantityInput(key);
        if (!currentInput || !owner.validQuantity(currentInput, amount)) {
          owner?.setLiveMessage(line, cartErrorMessage());
          return;
        }
        return owner.changeQuantity(key, currentInput.dataset.index, amount, event);
      };
      const drawer = document.querySelector('cart-drawer');
      const job = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : cartChangeQueue.then(task);
      cartChangeQueue = job.catch(() => {});
      return cartChangeQueue;
    }

    async changeQuantity(key, line, quantity, event) {
      const sections = this.getSectionsToRender();
      const focusWasInside = this.contains(document.activeElement);
      const focusId = focusWasInside ? document.activeElement?.id : null;
      this.setLoading(line, true);
      try {
        const state = await requestJson(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sections.map((item) => item.section), sections_url: window.location.pathname }),
        });
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count)) throw cartError();
        this.renderCartState(state, sections);
        this.setLiveMessage(line, '');
        const current = state.items.find((item) => item.key === key);
        if (quantity > 0 && current && current.quantity !== quantity) {
          this.setLiveMessage(line, (window.cartStrings?.quantityError || 'Only [quantity] available.').replace('[quantity]', current.quantity));
        }
        this.notifyCartUpdate(state, line, quantity);
      } catch (error) {
        cartChangeEpoch += 1;
        // Reconcile using GET only. Unknown server outcome must not double an order.
        const recovered = await this.recoverCart(sections);
        const message = error.userMessage || cartErrorMessage();
        this.setLiveMessage(line, message);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = message;
          errors.setAttribute('role', 'alert');
          if (!recovered) {
            const link = document.createElement('a');
            link.href = window.routes.cart_url;
            link.textContent = ' View cart';
            errors.append(link);
          }
        }
      } finally {
        this.setLoading(line, false);
        const owner = document.querySelector(this.tagName.toLowerCase());
        owner?.setLoading(line, false);
        if (focusWasInside && (!document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected)) {
          const target = owner?.findQuantityInput(key) || (focusId && document.getElementById(focusId))
            || document.querySelector('cart-drawer .drawer__close, #checkout');
          target?.focus();
        }
      }
    }

    renderCartState(state, sections) {
      // Shopify can return HTTP 200 with a null section: do not treat that as rendered.
      if (!sections.every((section) => typeof state.sections?.[section.section] === 'string' && state.sections[section.section].trim())) throw cartError();
      this.classList.toggle('is-empty', state.item_count === 0);
      document.querySelector('cart-items')?.classList.toggle('is-empty', state.item_count === 0);
      const drawer = document.querySelector('cart-drawer');
      drawer?.classList.toggle('is-empty', state.item_count === 0);
      drawer?.syncEmptyState?.();
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', state.item_count === 0);
      for (const section of sections) {
        if (!this.replaceSection(section, state.sections[section.section])) throw cartError();
      }
      document.querySelector('cart-drawer')?.bindControls?.();
      document.querySelector('cart-drawer')?.bindTrigger?.();
    }

    notifyCartUpdate(state, line, quantity) {
      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          Promise.resolve(window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state }))
            .catch((error) => console.error('Cart subscriber failed', error));
        }
        const owner = this.isConnected ? this : document.querySelector(this.tagName.toLowerCase());
        if (owner) dispatch(owner, 'cart:updated', { cart: state, line, quantity });
      } catch (error) { console.error('Cart subscriber failed', error); }
    }

    async recoverCart(sections) {
      try {
        const url = new URL(window.routes.cart_url, window.location.origin);
        const state = await requestJson(`${url.pathname}.js`, { cache: 'no-store' });
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count)) return false;
        url.searchParams.set('sections', sections.map((section) => section.section).join(','));
        state.sections = await requestJson(url.href, { cache: 'no-store' });
        this.renderCartState(state, sections);
        this.notifyCartUpdate(state);
        return true;
      } catch (_error) { return false; }
    }

    replaceSection(section, html) {
      if (!html) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (section.section === 'cart-icon-bubble') {
        const sourceBubble = parsed.querySelector('#cart-icon-bubble');
        const targetBubble = document.querySelector('#cart-icon-bubble');
        if (sourceBubble && targetBubble) {
          targetBubble.replaceWith(sourceBubble);
          document.querySelector('cart-drawer')?.bindTrigger?.();
          return true;
        }
        return false;
      }
      const source = parsed.querySelector(section.selector);
      const host = document.getElementById(section.id);
      // The subtotal block is optional. Never insert an entire section wrapper
      // into the footer when a merchant deliberately removed that block.
      if (section.id === 'main-cart-footer' && !source && !host?.querySelector(section.selector)) {
        return Boolean(host && parsed.getElementById('main-cart-footer'));
      }
      const target = host?.querySelector(section.selector) || host;
      if (!source || !target) return false;
      if (section.section === 'cart-drawer') document.querySelector('cart-drawer')?.preserveUpsellState(source, target);
      target.innerHTML = source.innerHTML;
      return true;
    }

    setLiveMessage(line, message) {
      const lineError = document.querySelector(`#Line-item-error-${line} .cart-item__error-text, #CartDrawer-LineItemError-${line} .cart-item__error-text`);
      if (lineError) lineError.textContent = message;
      const live = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
      if (live) {
        live.textContent = message;
        live.setAttribute('aria-hidden', String(!message));
      }
    }

    setLoading(line, loading) {
      this.classList.toggle('cart__items--disabled', loading);
      this.querySelectorAll(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner, #CartItem-${line} .loading-overlay__spinner, #CartDrawer-Item-${line} .loading-overlay__spinner`).forEach((spinner) => {
        spinner.classList.toggle('hidden', !loading);
      });
    }
  }
  define('cart-items', CartItems);
  define('cart-drawer-items', class CartDrawerItems extends CartItems {});

  class CartDiscountField extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.form = this.querySelector('.cart-discount-form');
      this.input = this.querySelector('input[name="discount"]');
      this.onSubmit = (event) => this.apply(event);
      this.onInput = () => this.clearMessage();
      this.onClick = (event) => {
        const button = event.target.closest('[data-discount-remove]');
        if (!button || !this.contains(button)) return;
        this.remove(button.dataset.discountRemove, event);
      };
      this.form?.addEventListener('submit', this.onSubmit);
      this.input?.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      this.removeEventListener('click', this.onClick);
    }

    codes() {
      return Array.from(document.querySelectorAll('cart-discount-field [data-discount-code]'))
        .map((item) => item.dataset.discountCode?.trim())
        .filter((code, index, codes) => code && codes.indexOf(code) === index);
    }

    message(type, text) {
      const current = document.getElementById(this.id) || this;
      const error = current.querySelector('[data-discount-error]');
      const status = current.querySelector('[data-discount-status]');
      if (error) {
        error.textContent = type === 'error' ? text : '';
        error.hidden = type !== 'error';
      }
      if (status) status.textContent = type === 'status' ? text : '';
      current.classList.toggle('cart-discount--error', type === 'error');
    }

    clearMessage() {
      this.message('', '');
      this.input?.removeAttribute('aria-invalid');
    }

    setBusy(busy) {
      const current = document.getElementById(this.id) || this;
      current.toggleAttribute('aria-busy', busy);
      current.querySelectorAll('button, input').forEach((control) => { control.disabled = busy; });
      current.querySelector('[data-discount-button-label]')?.classList.toggle('hidden', busy);
      current.querySelector('[data-discount-submit] .loading-overlay__spinner')?.classList.toggle('hidden', !busy);
    }

    apply(event) {
      event.preventDefault();
      event.stopPropagation();
      const code = this.input?.value.trim();
      if (!code) {
        this.input?.setAttribute('aria-invalid', 'true');
        this.message('error', this.dataset.emptyError || 'Please enter a discount code!');
        this.input?.focus();
        return;
      }
      const existing = this.codes();
      if (existing.some((item) => item.toLocaleUpperCase() === code.toLocaleUpperCase())) {
        this.input.value = '';
        this.message('status', `${code} is already applied.`);
        return;
      }
      this.queueUpdate([...existing, code], { action: 'apply', code });
    }

    remove(code, event) {
      event?.preventDefault();
      event?.stopPropagation();
      if (!code) return;
      const remaining = this.codes().filter((item) => item.toLocaleUpperCase() !== code.toLocaleUpperCase());
      this.queueUpdate(remaining, { action: 'remove', code });
    }

    queueUpdate(codes, operation) {
      const componentId = this.id;
      const job = cartChangeQueue.then(() => {
        const current = document.getElementById(componentId);
        return current?.updateDiscounts(codes, operation);
      });
      cartChangeQueue = job.catch(() => {});
      return job;
    }

    async updateDiscounts(codes, operation) {
      const owner = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
      if (!owner?.getSectionsToRender || !window.routes?.cart_update_url) {
        this.message('error', this.dataset.networkError || 'The discount could not be updated. Please try again.');
        return;
      }
      const sections = owner.getSectionsToRender();
      this.setBusy(true);
      this.clearMessage();
      try {
        const sectionPayload = {
          sections: sections.map((item) => item.section),
          sections_url: window.location.pathname,
        };
        const update = (discount, renderSections) => requestJson(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount, ...(renderSections ? sectionPayload : {}) }),
        });
        const replaceDiscounts = async (nextCodes) => {
          // Shopify keeps inapplicable codes in `discount_codes`. Clear first so
          // the submitted list is a deterministic replacement, not an append.
          if (!nextCodes.length) return update('', true);
          await update('', false);
          return update(nextCodes.join(','), true);
        };

        let state = operation.action === 'remove'
          ? await replaceDiscounts(codes)
          : await update(codes.join(','), true);
        if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();

        if (operation.action === 'apply') {
          const result = state.discount_codes.find((item) =>
            String(item.code).toLocaleUpperCase() === operation.code.toLocaleUpperCase()
          );
          if (!result || result.applicable === false) {
            const previousCodes = codes.filter((item) => item.toLocaleUpperCase() !== operation.code.toLocaleUpperCase());
            state = await replaceDiscounts(previousCodes);
            if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();
            owner.renderCartState(state, sections);
            this.refreshFields(state.sections);
            owner.notifyCartUpdate(state);

            const current = document.getElementById(this.id) || this;
            const input = current.querySelector('input[name="discount"]');
            if (input) {
              input.value = '';
              input.setAttribute('aria-invalid', 'true');
            }
            current.message('error', current.dataset.invalidError || 'This discount code is invalid or not applicable.');
            input?.focus();
            return;
          }

          const intended = codes.map((item) => item.toLocaleUpperCase());
          const hasResidualCode = state.discount_codes.some((item) =>
            item.applicable === false || !intended.includes(String(item.code).toLocaleUpperCase())
          );
          if (hasResidualCode) {
            state = await replaceDiscounts(codes);
            if (!Array.isArray(state.items) || !Number.isSafeInteger(state.item_count) || !Array.isArray(state.discount_codes)) throw cartError();
            const sanitized = state.discount_codes.every((item) =>
              item.applicable !== false && intended.includes(String(item.code).toLocaleUpperCase())
            );
            if (!sanitized) throw cartError();
          }
        }

        owner.renderCartState(state, sections);
        this.refreshFields(state.sections);
        owner.notifyCartUpdate(state);

        const current = document.getElementById(this.id);
        const rendered = Array.from(current?.querySelectorAll('[data-discount-code]') || []).some((item) =>
          item.dataset.discountCode?.toLocaleUpperCase() === operation.code.toLocaleUpperCase()
        );
        if (operation.action === 'apply' && !rendered) {
          current?.message('status', current.dataset.shippingMessage || 'Shipping discounts are calculated at checkout.');
        } else {
          current?.message('status', operation.action === 'apply' ? `${operation.code} applied.` : `${operation.code} removed.`);
        }
        const focusTarget = current?.querySelector('input[name="discount"]');
        if (focusTarget) {
          focusTarget.value = '';
          focusTarget.focus();
        }
      } catch (error) {
        const recovered = await owner.recoverCart(sections);
        const current = document.getElementById(this.id) || this;
        current.message('error', current.dataset.networkError || 'The discount could not be updated. Please try again.');
        if (!recovered) dispatch(current, 'cart:error', { message: error.userMessage || cartErrorMessage() });
      } finally {
        (document.getElementById(this.id) || this).setBusy(false);
      }
    }

    refreshFields(sectionHtml) {
      const sources = new Map();
      Object.values(sectionHtml || {}).forEach((html) => {
        if (typeof html !== 'string') return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll('cart-discount-field[id]').forEach((field) => sources.set(field.id, field));
      });
      document.querySelectorAll('cart-discount-field[id]').forEach((field) => {
        const source = sources.get(field.id);
        if (source && !field.isEqualNode(source)) field.replaceWith(document.importNode(source, true));
      });
    }
  }
  define('cart-discount-field', CartDiscountField);

  class CartNote extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('input', () => {
        this.dataset.dirty = 'true';
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.flush().catch((error) => {
          dispatch(this.closest('cart-drawer') || this, 'cart:error', { message: error.message });
        }), 300);
      });
    }

    async flush() {
      clearTimeout(this.noteTimer);
      if (this.savePromise) await this.savePromise;
      if (this.dataset.dirty !== 'true') return;
      const value = this.querySelector('textarea')?.value || '';
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const task = async () => {
        const response = await postJson(window.routes.cart_update_url, { note: value });
        const result = await parseJsonResponse(response, 'Cart note could not be saved.');
        if (!response.ok || result.status || result.errors) throw new Error('Cart note could not be saved.');
        delete this.dataset.dirty;
      };
      this.savePromise = drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : task();
      try { await this.savePromise; }
      finally { this.savePromise = null; }
    }

    disconnectedCallback() {
      clearTimeout(this.noteTimer);
    }
  }
  define('cart-note', CartNote);

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.upsellIntentMap = new Map();
      this.upsellIntentTimer = null;
      this.upsellCommitPromise = null;
      this.cartStateStale = true;
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
      this.addEventListener('submit', (event) => this.onCheckoutSubmit(event));
      this.onCartUpdated = (event) => {
        if (event.detail?.source === 'cart-drawer-upsells') return;
        if (isFullCart(event.detail?.cart)) {
          this.lastKnownCart = event.detail.cart;
          this.cartStateStale = false;
        }
        window.queueMicrotask(() => this.syncPreselectedUpsells());
      };
      this.onCartError = (event) => this.showCartError(event.detail?.message);
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.addEventListener('cart:error', this.onCartError);
      this.bindControls();
      this.bindTrigger();
      this.syncEmptyState();
      window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    open(trigger) {
      this.activeElement = trigger || document.activeElement;
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      this.querySelector('.drawer__inner')?.focus();
      dispatch(this, 'cart-drawer:open');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      this.activeElement?.focus?.();
      dispatch(this, 'cart-drawer:close');
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    syncEmptyState() {
      const empty = this.classList.contains('is-empty');
      this.querySelectorAll('.drawer__header, .cart-drawer__body, .drawer__footer').forEach((region) => {
        region.toggleAttribute('inert', empty);
        region.setAttribute('aria-hidden', String(empty));
      });
      this.querySelectorAll('.drawer__inner-empty').forEach((region) => {
        region.toggleAttribute('inert', !empty);
        region.setAttribute('aria-hidden', String(!empty));
      });
    }

    bindControls() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && overlay.dataset.cartDrawerBound !== 'true') {
        overlay.dataset.cartDrawerBound = 'true';
        overlay.addEventListener('click', () => this.close());
      }

      this.querySelectorAll('.drawer__close, [data-cart-drawer-close]').forEach((button) => {
        if (button.dataset.cartDrawerBound === 'true') return;
        button.dataset.cartDrawerBound = 'true';
        button.addEventListener('click', () => this.close());
      });
    }

    bindTrigger() {
      const icon = document.getElementById('cart-icon-bubble');
      // The visible/clickable hit area is the wrapping link in this theme.
      // Binding only the inner bubble leaves most clicks navigating to /cart.
      const trigger = icon?.closest('a, button') || icon;
      if (trigger && trigger.dataset.cartDrawerBound !== 'true') {
        trigger.dataset.cartDrawerBound = 'true';
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(trigger);
        });
      }
    }

    async prepareCheckout() {
      await this.flushUpsellIntents();
      await this.waitForCartMutations();
      for (const note of this.querySelectorAll('cart-note')) await note.flush?.();
      for (const gift of this.querySelectorAll('cart-drawer-gift')) {
        const result = await gift.update?.();
        if (result?.error) throw result.error;
      }
      await this.waitForCartMutations();
      const result = await this.syncPreselectedUpsells();
      if (result?.error) throw result.error;
      await this.waitForCartMutations();
      return result;
    }

    preserveUpsellState(source, target) {
      const currentByIdentity = new Map();
      target.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((upsell) => {
        const identity = upsell.dataset.upsellKey || upsell.dataset.handle;
        if (!identity) return;
        const matches = currentByIdentity.get(identity) || [];
        matches.push(upsell);
        currentByIdentity.set(identity, matches);
      });

      source.querySelectorAll('cart-drawer-upsell[data-handle]').forEach((replacement) => {
        const identity = replacement.dataset.upsellKey || replacement.dataset.handle;
        const matches = currentByIdentity.get(identity);
        const current = matches?.shift();
        if (!current) return;

        const selected = current.dataset.selected === 'true';
        replacement.dataset.selected = String(selected);
        replacement.dataset.selectionHydrated = current.dataset.selectionHydrated || 'true';
        replacement.querySelectorAll('.upsell-toggle-btn').forEach((button) => {
          button.setAttribute('aria-pressed', String(selected));
        });

        if (current.dataset.id) replacement.dataset.id = current.dataset.id;
        const replacementInput = replacement.querySelector('product-form input[name="id"]');
        const currentInput = current.querySelector('product-form input[name="id"]');
        if (replacementInput && currentInput) replacementInput.value = currentInput.value;

        const currentSelects = Array.from(current.querySelectorAll('.upsell__variant-picker select'));
        replacement.querySelectorAll('.upsell__variant-picker select').forEach((select, index) => {
          if (currentSelects[index]) select.value = currentSelects[index].value;
        });
      });
    }

    renderContents(state, options = {}) {
      if (isFullCart(state)) {
        this.lastKnownCart = state;
        this.cartStateStale = false;
      } else if (state?.sections) {
        // add.js returns line items and rendered sections, but not a complete cart.
        // Keep the server-rendered HTML while forcing the next stateful operation to verify once.
        this.cartStateStale = true;
      }
      const drawerHtml = state.sections?.['cart-drawer'];
      const bubbleHtml = state.sections?.['cart-icon-bubble'];
      const parser = new DOMParser();
      if (drawerHtml) {
        const source = parser.parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
        const target = this.querySelector('#CartDrawer');
        if (source && target) {
          this.preserveUpsellState(source, target);
          target.innerHTML = source.innerHTML;
          // Section HTML is authoritative for both add.js and change.js responses.
          // add.js does not necessarily include the total cart item_count.
          const renderedDrawer = source.closest('cart-drawer');
          if (renderedDrawer) {
            this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
          } else if (Number.isFinite(state.item_count)) {
            this.classList.toggle('is-empty', state.item_count === 0);
          } else {
            this.classList.toggle('is-empty', Boolean(source.querySelector('.drawer__inner-empty')));
          }
          this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());
          if (this.dataset.upsellBusy === 'true') this.setUpsellBusy(true);
        }
      }
      if (bubbleHtml) {
        const sourceDocument = parser.parseFromString(bubbleHtml, 'text/html');
        const source = sourceDocument.querySelector('#cart-icon-bubble') || sourceDocument.querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      }
      this.syncEmptyState();
      this.bindControls();
      this.bindTrigger();
      if (options.open !== false) this.open();
      if (options.reconcile !== false) window.queueMicrotask(() => this.syncPreselectedUpsells());
    }

    getToggleUpsells() {
      return Array.from(this.querySelectorAll('cart-drawer-upsell[data-toggle="true"][data-product-id][data-id]'));
    }

    getUpsellIntentKey(upsell) {
      return upsell?.dataset.upsellKey
        || upsell?.dataset.handle
        || `${upsell?.dataset.productId || ''}:${upsell?.dataset.id || ''}`;
    }

    setUpsellBusy(busy) {
      if (busy) this.dataset.upsellBusy = 'true';
      else delete this.dataset.upsellBusy;
      this.getToggleUpsells().forEach((upsell) => {
        const button = upsell.querySelector('.upsell-toggle-btn');
        if (!button) return;
        button.disabled = busy;
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
      this.querySelectorAll('[name="checkout"], .shopify-payment-button button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart').forEach((control) => {
        if (busy) {
          control.setAttribute('aria-disabled', 'true');
          control.setAttribute('data-cart-runtime-disabled', 'true');
          if ('disabled' in control) control.disabled = true;
          else control.inert = true;
        } else if (control.dataset.cartRuntimeDisabled === 'true') {
          control.removeAttribute('aria-disabled');
          delete control.dataset.cartRuntimeDisabled;
          if ('disabled' in control) control.disabled = false;
          else control.inert = false;
        }
      });
    }

    showCartError(message = '') {
      const target = this.querySelector('#CartDrawer-CartErrors, .cart-errors, [data-cart-errors]');
      if (!target) return;
      target.textContent = message || window.cartStrings?.error || 'There was an error updating your cart. Please try again.';
      target.hidden = false;
      target.setAttribute('role', 'alert');
    }

    enqueueCartMutation(task) {
      if (typeof task !== 'function') return Promise.resolve({ changed: false, cart: null });
      this.setUpsellBusy(true);
      const previous = this.cartMutationQueue || Promise.resolve();
      const queueId = this.debugQueueSequence = (this.debugQueueSequence || 0) + 1;
      const queuedAt = Date.now();
      window.CartDebug?.log('cart-queue.enqueued', { queueId, pending: !!this.cartMutationQueue });
      const tracked = previous.catch(() => undefined).then(() => {
        window.CartDebug?.log('cart-queue.start', { queueId, waitMs: Date.now() - queuedAt });
        return task();
      });
      tracked.then(
        () => window.CartDebug?.log('cart-queue.complete', { queueId, elapsedMs: Date.now() - queuedAt }),
        (error) => window.CartDebug?.log('cart-queue.error', { queueId, code: error?.code, elapsedMs: Date.now() - queuedAt })
      );
      this.cartMutationQueue = tracked;
      this.cartMutationPromise = tracked;
      const clear = () => {
        if (this.cartMutationQueue === tracked) {
          this.cartMutationQueue = null;
          this.cartMutationPromise = null;
          this.setUpsellBusy(false);
        }
      };
      tracked.then(clear, clear);
      return tracked;
    }

    enqueueUpsellMutation(task) {
      return this.enqueueCartMutation(task);
    }

    queueUpsellIntent(upsell, selected) {
      if (!upsell) return Promise.resolve({ changed: false, cart: this.lastKnownCart || null });
      const key = this.getUpsellIntentKey(upsell);
      this.upsellIntentMap ||= new Map();
      this.upsellIntentMap.set(key, {
        key,
        selected: Boolean(selected),
      });
      this.persistUpsellDecision(this.lastKnownCart, upsell, selected);
      if (selected) this.saveNestedBinding(this.lastKnownCart, upsell, null);

      window.clearTimeout(this.upsellIntentTimer);
      this.upsellIntentTimer = window.setTimeout(() => {
        this.upsellIntentTimer = null;
        this.flushUpsellIntents().catch(() => undefined);
      }, 300);
      return this.upsellCommitPromise || Promise.resolve({ changed: false, cart: this.lastKnownCart || null });
    }

    flushUpsellIntents() {
      window.clearTimeout(this.upsellIntentTimer);
      this.upsellIntentTimer = null;
      if (this.upsellCommitPromise) return this.upsellCommitPromise;
      if (!this.upsellIntentMap?.size) return Promise.resolve({ changed: false, cart: this.lastKnownCart || null });

      const intents = this.upsellIntentMap;
      this.upsellIntentMap = new Map();
      const task = async () => {
        try {
          return await this.reconcileUpsells({ overrides: intents, useCachedCart: true });
        } catch (error) {
          // If a write succeeded but its verification failed, keep the server-rendered
          // section visible and block checkout until the cart can be re-verified.
          // Only roll back the UI when no cart mutation was accepted by Shopify.
          if (error.cartMutationApplied) {
            this.cartStateStale = true;
          } else {
            intents.forEach((intent) => {
              const current = this.findUpsell(intent.key);
              if (current) {
                current.setSelected(!intent.selected);
                this.persistUpsellDecision(this.lastKnownCart, current, !intent.selected);
              }
            });
          }
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          throw error;
        }
      };
      const tracked = this.enqueueCartMutation(task);
      this.upsellCommitPromise = tracked;
      const clearCommit = () => {
        if (this.upsellCommitPromise === tracked) this.upsellCommitPromise = null;
        if (this.upsellIntentMap?.size) {
          this.upsellIntentTimer = window.setTimeout(() => {
            this.upsellIntentTimer = null;
            this.flushUpsellIntents().catch(() => undefined);
          }, 0);
        }
      };
      tracked.then(clearCommit, clearCommit);
      return tracked;
    }

    findUpsell(identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      const candidates = this.getToggleUpsells();
      if (key) {
        const keyed = candidates.find((upsell) => this.getUpsellIntentKey(upsell) === key);
        if (keyed) return keyed;
      }
      return candidates.find((upsell) => (
        upsell.dataset.handle === identity.handle
        && Number(upsell.dataset.productId) === Number(identity.productId)
        && Number(upsell.dataset.id) === Number(identity.variantId)
      )) || null;
    }

    async waitForCartMutations() {
      let pending = this.cartMutationPromise;
      let result = null;
      while (pending) {
        result = await pending;
        if (result?.error) throw result.error;
        if (pending === this.cartMutationPromise) break;
        pending = this.cartMutationPromise;
      }
      return result;
    }

    waitForUpsellMutations() {
      return this.waitForCartMutations();
    }

    findCartUpsellItems(cartItems, identity = {}) {
      const key = typeof identity === 'string' ? identity : identity.key;
      if (!key) return [];
      return cartItems.filter((item) => itemProperty(item, '_theme_upsell_key') === String(key));
    }

    isDecorativeCartItem(item) {
      return Boolean(
        itemProperty(item, '_theme_upsell_key')
        || itemProperty(item, '_theme_gift_key')
        || itemProperty(item, '_theme_additional_key')
      );
    }

    hasPrimaryCartItem(cart) {
      return (cart?.items || []).some((item) => !this.isDecorativeCartItem(item));
    }

    findInvalidAdditionalItem(cart) {
      const items = cart?.items || [];
      if (!this.hasPrimaryCartItem(cart)) return items.find((item) => this.isDecorativeCartItem(item)) || null;
      return items.find((item) => {
        if (!itemProperty(item, '_theme_additional_key')) return false;
        const parentProductId = Number(itemProperty(item, '_theme_parent_product_id'));
        const minimum = Math.max(1, Number(itemProperty(item, '_theme_min_parent_quantity')) || 1);
        if (!parentProductId) return false;
        const parentQuantity = items.reduce((sum, candidate) => {
          if (this.isDecorativeCartItem(candidate)) return sum;
          return Number(candidate.product_id) === parentProductId
            ? sum + Number(candidate.quantity || 0)
            : sum;
        }, 0);
        return parentQuantity < minimum;
      }) || null;
    }

    async removeInvalidAdditionalItems(cart) {
      let state = cart;
      let changed = false;
      let invalid = this.findInvalidAdditionalItem(state);
      let attemptsRemaining = Math.max(2, (state?.items?.length || 0) + 1);
      while (invalid && attemptsRemaining > 0) {
        const response = await postJson(window.routes?.cart_change_url || '/cart/change.js', {
          id: invalid.key,
          quantity: 0,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });
        const result = await parseJsonResponse(response, 'Unable to remove an ineligible additional item');
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || 'Unable to remove an ineligible additional item');
        }
        if (result.sections) this.renderContents(result, { open: false, reconcile: false });
        state = isFullCart(result) ? result : await this.readCart();
        changed = true;
        attemptsRemaining -= 1;
        invalid = this.findInvalidAdditionalItem(state);
      }
      if (invalid) throw new Error('Additional item eligibility could not be synchronized');
      return { changed, cart: state };
    }

    getUpsellDecisionKey(cart, upsell) {
      const themeId = this.dataset.themeId || 'theme';
      return `theme-upsell-choice-v2:${themeId}:${upsell.dataset.upsellKey || upsell.dataset.handle || ''}:${this.upsellConfiguration(upsell)}`;
    }

    upsellConfiguration(upsell) {
      return JSON.stringify([
        upsell.dataset.upsellKey || '',
        upsell.dataset.productId || '',
        upsell.dataset.defaultSelected || 'false',
        upsell.dataset.style || '',
        upsell.dataset.toggle || '',
      ]);
    }

    readUpsellDecision(cart, upsell) {
      const key = this.getUpsellDecisionKey(cart, upsell);
      if (window.__cartUpsellChoices?.has(key)) return window.__cartUpsellChoices.get(key);
      try {
        return window.sessionStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    }

    persistUpsellDecision(cart, upsell, selected) {
      const key = this.getUpsellDecisionKey(cart, upsell);
      window.__cartUpsellChoices ||= new Map();
      window.__cartUpsellChoices.set(key, String(Boolean(selected)));
      try {
        window.sessionStorage.setItem(key, String(Boolean(selected)));
      } catch (_error) {}
    }

    observeEditorUpsellConfiguration(upsell) {
      const key = `theme-upsell-config-v2:${this.dataset.themeId || 'theme'}:${upsell.dataset.upsellKey}`;
      const config = this.upsellConfiguration(upsell);
      window.__cartUpsellEditorConfigs ||= new Map();
      let previous = window.__cartUpsellEditorConfigs.get(key);
      try {
        if (previous === undefined) previous = sessionStorage.getItem(key);
        sessionStorage.setItem(key, config);
      } catch (_error) {}
      window.__cartUpsellEditorConfigs.set(key, config);
      if (previous !== config) {
        upsell.dataset.selectionConfigChanged = 'true';
        upsell.dataset.selectionHydrated = 'false';
        upsell.setSelected?.(upsell.dataset.defaultSelected === 'true');
      }
    }

    hydrateUpsellSelections(cart) {
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      this.getToggleUpsells().forEach((upsell) => {
        this.observeEditorUpsellConfiguration(upsell);
        if (hasPrimaryItem && upsell.dataset.selectionHydrated === 'true' && upsell.dataset.selectionConfigChanged !== 'true') return;
        const saved = this.readUpsellDecision(cart, upsell);
        const configurationChanged = upsell.dataset.selectionConfigChanged === 'true';
        let selected = configurationChanged
          ? upsell.dataset.defaultSelected === 'true'
          : saved !== null ? saved === 'true'
            : upsell.dataset.defaultSelected === 'true';
        if (configurationChanged || saved === null) this.persistUpsellDecision(cart, upsell, selected);
        delete upsell.dataset.selectionConfigChanged;
        if (!hasPrimaryItem) selected = false;
        upsell.setSelected?.(selected);
        upsell.dataset.selectionHydrated = String(hasPrimaryItem);
      });
    }

    nestedParentSignature(item) {
      return JSON.stringify([
        String(item.variant_id || item.id),
        Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
        String(item.selling_plan_allocation?.selling_plan?.id || ''),
      ]);
    }

    nestedBindingKey(cart, upsell) {
      return 'theme-nested-upsell:' + String(cart?.token || 'cart').split('?')[0]
        + ':' + upsell.dataset.upsellKey;
    }

    readNestedBinding(cart, upsell) {
      const key = this.nestedBindingKey(cart, upsell);
      this.nestedBindings ||= new Map();
      if (this.nestedBindings.has(key)) return this.nestedBindings.get(key);
      try {
        const binding = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        this.nestedBindings.set(key, binding);
        return binding;
      } catch (_error) { return null; }
    }

    saveNestedBinding(cart, upsell, binding) {
      const key = this.nestedBindingKey(cart, upsell);
      this.nestedBindings ||= new Map();
      this.nestedBindings.set(key, binding);
      try {
        if (binding) window.sessionStorage.setItem(key, JSON.stringify(binding));
        else window.sessionStorage.removeItem(key);
      } catch (_error) {}
    }

    eligibleNestedParent(item, variantId) {
      return Boolean(item?.key) && !this.isDecorativeCartItem(item)
        && Number(item.variant_id || item.id) !== Number(variantId)
        && !Array.from(this.querySelectorAll('cart-drawer-gift[data-id]')).some((gift) =>
          Number(gift.dataset.id) === Number(item.variant_id || item.id))
        && !item.has_components && !item.item_components?.length;
    }

    findNestedParent(cart, upsell) {
      const items = cart?.items || [];
      const binding = this.readNestedBinding(cart, upsell);
      if (binding) {
        const parent = items.find((item) => this.eligibleNestedParent(item, upsell.dataset.id)
          && (item.key === binding.key || this.nestedParentSignature(item) === binding.signature));
        if (parent) return parent;
        this.saveNestedBinding(cart, upsell, null);
      }
      return items.find((item) => this.eligibleNestedParent(item, upsell.dataset.id)) || null;
    }

    prepareNestedUpsells(cart) {
      this.getToggleUpsells().forEach((upsell) => {
        const nested = this.findCartUpsellItems(cart.items || [], upsell.dataset.upsellKey)
          .find((item) => item.parent_relationship?.parent_key);
        if (nested) {
          const parent = cart.items.find((item) => item.key === nested.parent_relationship.parent_key);
          if (parent) this.saveNestedBinding(cart, upsell, {
            key: parent.key, signature: this.nestedParentSignature(parent),
            defaultSelected: upsell.dataset.defaultSelected,
          });
        }
        const binding = this.readNestedBinding(cart, upsell);
        if (!binding) return;
        if (window.Shopify?.designMode && binding.defaultSelected !== upsell.dataset.defaultSelected) {
          this.saveNestedBinding(cart, upsell, null);
          return;
        }
        if (!this.findNestedParent(cart, upsell)) {
          upsell.setSelected(false);
          upsell.dataset.selectionHydrated = 'false';
          this.saveNestedBinding(cart, upsell, null);
        }
      });
    }

    buildNestedUpsellItem(upsell, cart) {
      const parent = this.findNestedParent(cart, upsell);
      if (!parent) throw new Error('Add an eligible main product before selecting this add-on.');
      const item = {
        id: Number(upsell.dataset.id), quantity: 1,
        parent_line_key: parent.key,
        properties: { _theme_upsell_key: upsell.dataset.upsellKey || '' },
      };
      const sellingPlan = upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value;
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      return item;
    }

    findUpsellMismatch(cart) {
      this.prepareNestedUpsells(cart);
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      for (const upsell of this.getToggleUpsells()) {
        const variantId = Number(upsell.dataset.id);
        const markedItems = this.findCartUpsellItems(cartItems, upsell.dataset.upsellKey || '');
        const desiredItem = markedItems.find((item) => Number(item.variant_id || item.id) === variantId);
        const staleItem = markedItems.find((item) => Number(item.variant_id || item.id) !== variantId);
        const selected = hasPrimaryItem && upsell.dataset.selected === 'true';
        if (selected && staleItem) return { action: 'remove', upsell, cart, cartItem: staleItem };
        if (selected && !desiredItem) {
          if (!this.findNestedParent(cart, upsell)) {
            upsell.setSelected(false);
            continue;
          }
          return { action: 'add', upsell, cart };
        }
        if (selected && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
          return { action: 'change', upsell, cart, cartItem: desiredItem, quantity: 1 };
        }
        if (!selected && markedItems[0]) return { action: 'remove', upsell, cart, cartItem: markedItems[0] };
      }
      return null;
    }

    async applyUpsellMismatch(mismatch) {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const payload = mismatch.action === 'add'
        ? {
            items: [this.buildNestedUpsellItem(mismatch.upsell, mismatch.cart)],
            sections,
            sections_url: window.location.pathname + window.location.search,
          }
        : {
            id: mismatch.cartItem?.key,
            quantity: mismatch.action === 'change' ? Number(mismatch.quantity || 1) : 0,
            sections,
            sections_url: window.location.pathname + window.location.search,
          };
      if (mismatch.action !== 'add' && !payload.id) {
        throw new Error('Unable to identify the selected cart upsell');
      }
      const route = mismatch.action === 'add'
        ? (window.routes?.cart_add_url || '/cart/add.js')
        : (window.routes?.cart_change_url || '/cart/change.js');
      const response = await postJson(route, payload);
      const state = await parseJsonResponse(response, 'Unable to synchronize cart upsell');
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'Unable to synchronize cart upsell');
      }
      if (mismatch.action === 'add') {
        const parent = mismatch.cart.items.find((item) => item.key === payload.items[0].parent_line_key);
        this.saveNestedBinding(mismatch.cart, mismatch.upsell, {
          key: parent.key, signature: this.nestedParentSignature(parent),
          defaultSelected: mismatch.upsell.dataset.defaultSelected,
        });
      }
      if (state.sections) this.renderContents(state, { open: false, reconcile: false });
      return mismatch.action !== 'add' && isFullCart(state) ? state : this.readCart();
    }

    buildUpsellBatch(cart, overrides = null) {
      this.prepareNestedUpsells(cart);
      const cartItems = Array.isArray(cart?.items) ? cart.items : [];
      const hasPrimaryItem = this.hasPrimaryCartItem(cart);
      const updates = {};
      const additions = [];
      const bindings = [];

      this.getToggleUpsells().forEach((upsell) => {
        const key = this.getUpsellIntentKey(upsell);
        const intent = overrides?.get?.(key);
        const requestedSelected = intent ? intent.selected : upsell.dataset.selected === 'true';
        const selected = hasPrimaryItem && requestedSelected;
        const markedItems = this.findCartUpsellItems(cartItems, upsell.dataset.upsellKey || '');
        const variantId = Number(upsell.dataset.id);
        const desiredItem = markedItems.find((item) => Number(item.variant_id || item.id) === variantId);

        if (selected && !desiredItem) {
          const parent = this.findNestedParent(cart, upsell);
          if (!parent) {
            upsell.setSelected(false);
            this.persistUpsellDecision(cart, upsell, false);
          } else {
            const item = this.buildNestedUpsellItem(upsell, cart);
            additions.push(item);
            bindings.push({ upsell, parent });
          }
        }

        if (selected && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
          updates[desiredItem.key] = 1;
        }

        // Remove stale variants and duplicate lines in the same batch. The line item key
        // is stable even when another line is removed before this request is applied.
        markedItems.forEach((item) => {
          const keep = selected && desiredItem && item === desiredItem;
          if (!keep && item.key) updates[item.key] = 0;
        });
      });

      return { updates, additions, bindings };
    }

    async applyUpsellBatch(cart, overrides = null) {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      const sectionsUrl = window.location.pathname + window.location.search;
      const batch = this.buildUpsellBatch(cart, overrides);
      let state = cart;
      let changed = false;

      const renderResponse = (responseState, message) => {
        if (!responseState.sections?.['cart-drawer']) {
          const error = new Error(`${message} did not return the cart drawer section.`);
          error.cartMutationApplied = true;
          throw error;
        }
        this.renderContents(responseState, { open: false, reconcile: false });
      };

      if (Object.keys(batch.updates).length) {
        const response = await postJson(window.routes?.cart_update_url || '/cart/update.js', {
          updates: batch.updates,
          sections,
          sections_url: sectionsUrl,
        });
        const updateState = await parseJsonResponse(response, 'Unable to update cart upsells');
        if (!response.ok || updateState.status || updateState.errors) {
          throw new Error(updateState.description || updateState.message || updateState.errors || 'Unable to update cart upsells');
        }
        renderResponse(updateState, 'Cart update');
        if (!isFullCart(updateState)) {
          const error = new Error('Cart update did not return a complete cart.');
          error.cartMutationApplied = true;
          throw error;
        }
        state = updateState;
        changed = true;
      }

      if (batch.additions.length) {
        const response = await postJson(window.routes?.cart_add_url || '/cart/add.js', {
          items: batch.additions,
          sections,
          sections_url: sectionsUrl,
        });
        const addState = await parseJsonResponse(response, 'Unable to add cart upsells');
        if (!response.ok || addState.status || addState.errors) {
          throw new Error(addState.description || addState.message || addState.errors || 'Unable to add cart upsells');
        }
        renderResponse(addState, 'Cart add');
        batch.bindings.forEach(({ upsell, parent }) => {
          this.saveNestedBinding(state, upsell, {
            key: parent.key,
            signature: this.nestedParentSignature(parent),
            defaultSelected: upsell.dataset.defaultSelected,
          });
        });
        // add.js returns the added line items plus rendered HTML, not a complete cart.
        // One verification GET preserves exact totals for the next operation and checkout.
        try {
          state = await this.readCart();
        } catch (error) {
          error.cartMutationApplied = true;
          throw error;
        }
        changed = true;
      }

      if (isFullCart(state)) {
        this.lastKnownCart = state;
        this.cartStateStale = false;
      }
      return { changed, cart: state };
    }

    async readCart() {
      const cartRoute = window.routes?.cart_url || '/cart';
      const response = await fetchCartResponse(`${cartRoute.replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await parseJsonResponse(response, 'Unable to verify cart');
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Unable to verify cart');
      this.lastKnownCart = cart;
      this.cartStateStale = false;
      return cart;
    }

    async reconcileUpsells(options = {}) {
      const canUseCachedCart = options.useCachedCart
        && isFullCart(this.lastKnownCart)
        && !this.cartStateStale;
      let cart = canUseCachedCart ? this.lastKnownCart : await this.readCart();
      const additionalResult = await this.removeInvalidAdditionalItems(cart);
      cart = additionalResult.cart;
      if (!this.hasPrimaryCartItem(cart)
        || this.getToggleUpsells().some((upsell) => upsell.dataset.selectionHydrated !== 'true')) {
        this.hydrateUpsellSelections(cart);
      }
      const batchResult = await this.applyUpsellBatch(cart, options.overrides || null);
      cart = batchResult.cart;
      const changed = additionalResult.changed || batchResult.changed;
      if (changed) {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsells',
            cartData: cart,
          });
        }
        dispatch(this, 'cart:updated', { cart, source: 'cart-drawer-upsells' });
      }
      return { changed, cart };
    }

    syncPreselectedUpsells() {
      if (this.upsellCommitPromise) return this.upsellCommitPromise;
      if (this.upsellIntentMap?.size) return this.flushUpsellIntents();
      if (this.upsellSyncPromise) return this.upsellSyncPromise;

      const syncTask = async () => {
        try {
          return await this.reconcileUpsells({
            useCachedCart: isFullCart(this.lastKnownCart) && !this.cartStateStale,
          });
        } catch (error) {
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          return { changed: false, cart: null, error };
        }
      };
      this.upsellSyncPromise = this.enqueueCartMutation(syncTask);
      const clearSync = () => {
        this.upsellSyncPromise = null;
      };
      this.upsellSyncPromise.then(clearSync, clearSync);
      return this.upsellSyncPromise;
    }

    async onCheckoutSubmit(event) {
      const form = event.target.closest('form');
      const checkoutButton = event.submitter?.name === 'checkout'
        ? event.submitter
        : Array.from(this.querySelectorAll('[name="checkout"]')).find((button) => button.form === form);
      if (!form || !checkoutButton || this.dataset.upsellCheckoutCommit === 'true') return;

      event.preventDefault();
      if (this.dataset.upsellCheckoutRunning === 'true') return;
      this.dataset.upsellCheckoutRunning = 'true';

      try {
        await this.flushUpsellIntents();
        await this.waitForCartMutations();
        for (const note of this.querySelectorAll('cart-note')) await note.flush?.();
        await this.waitForCartMutations();
        for (const gift of this.querySelectorAll('cart-drawer-gift')) {
          const result = await gift.update?.();
          if (result?.error) throw result.error;
        }
        await this.waitForCartMutations();
        const syncResult = this.upsellIntentMap?.size || this.cartStateStale
          ? await this.syncPreselectedUpsells()
          : { changed: false, cart: this.lastKnownCart };
        if (syncResult?.error) throw syncResult.error;
        await this.waitForCartMutations();
        this.dataset.upsellCheckoutCommit = 'true';
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = checkoutButton.value || 'Checkout';
        checkoutInput.dataset.upsellCheckoutCommit = 'true';
        // Target sections can replace the checkout form during synchronization.
        const currentForm = this.querySelector('[name="checkout"]')?.form || (form.isConnected ? form : null);
        if (!currentForm) throw new Error('Cart is not ready for checkout.');
        currentForm.append(checkoutInput);
        HTMLFormElement.prototype.submit.call(currentForm);
      } catch (error) {
        delete this.dataset.upsellCheckoutCommit;
        delete this.dataset.upsellCheckoutRunning;
        dispatch(this, 'cart:error', { message: error.message });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      this.removeEventListener('cart:error', this.onCartError);
    }
  }
  define('cart-drawer', CartDrawer);

  class CartDrawerUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      window.queueMicrotask(() => {
        this.placeInConfiguredRegion();
      });
    }

    placeInConfiguredRegion() {
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (!drawer) return;
      const selector = this.dataset.position === 'footer' ? '.drawer__footer' : '.cart-drawer__body';
      const target = drawer.querySelector(selector);
      if (target && !target.contains(this)) target.append(this);
    }

  }
  define('cart-drawer-upsells', CartDrawerUpsells);

  class CartDrawerUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant({ reconcileCart: true }));
      });
      this.toggleButton?.addEventListener('click', (event) => {
        if (event.target.closest('select, option, input, a, product-form')) return;
        event.preventDefault();
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        const desiredSelected = this.dataset.selected !== 'true';
        drawer?.persistUpsellDecision(null, this, desiredSelected);
        this.dataset.selectionHydrated = 'true';
        delete this.dataset.selectionConfigChanged;
        this.setSelected(desiredSelected);
        drawer?.queueUpsellIntent(this, desiredSelected);
      });

      const drawer = this.closest('cart-drawer');
      drawer?.observeEditorUpsellConfiguration(this);
      this.setSelected(this.dataset.selected === 'true');
      this.updateVariant();
    }

    updateVariant(options = {}) {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, this.variants, {
        skipUnavailable: this.dataset.skipUnavailable === 'true',
        skipNonExistent: this.dataset.skipNonExistent === 'true',
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        this.setSelected(false);
        this.toggleButton?.setAttribute('aria-disabled', 'true');
        if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = true;
        if (options.reconcileCart) {
          const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
          drawer?.syncPreselectedUpsells();
        }
        return;
      }
      const previousVariantId = Number(this.dataset.id);
      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = 'true';
      this.toggleButton?.removeAttribute('aria-disabled');
      if (this.toggleButton?.tagName === 'BUTTON') this.toggleButton.disabled = false;
      if (this.variantInput) this.variantInput.value = String(variant.id);

      if (this.dataset.updatePrices === 'true') {
        const price = Number(variant.price || 0);
        const comparePrice = Number(variant.compare_at_price) > Number(variant.price)
          ? Number(variant.compare_at_price)
          : Number(variant.price || 0);
        this.querySelector('.regular-price')?.replaceChildren(formatMoney(price, this.dataset.moneyFormat));
        const compare = this.querySelector('.compare-price');
        if (compare) {
          compare.replaceChildren(formatMoney(comparePrice, this.dataset.moneyFormat));
          compare.classList.toggle('hidden', comparePrice <= price);
        }
      }

      if (
        options.reconcileCart
        && this.dataset.selected === 'true'
        && previousVariantId
        && previousVariantId !== Number(variant.id)
      ) {
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        drawer?.syncPreselectedUpsells();
      }
    }

    setSelected(selected) {
      this.dataset.selected = String(selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
    }

    getIdentity() {
      return {
        key: this.dataset.upsellKey || '',
        handle: this.dataset.handle || '',
        productId: Number(this.dataset.productId),
        variantId: Number(this.dataset.id),
      };
    }

    async toggle(desiredSelected = this.dataset.selected !== 'true') {
      if (this.dataset.loading === 'true') return { changed: false, cart: null };
      if (this.dataset.variantAvailable === 'false') return { changed: false, cart: null };
      this.dataset.loading = 'true';
      const wasSelected = this.dataset.selected === 'true';
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const identity = this.getIdentity();
      const wasHydrated = this.dataset.selectionHydrated === 'true';
      // Explicit input takes priority even if initial hydration is still pending.
      this.dataset.selectionHydrated = 'true';
      this.setSelected(desiredSelected);

      try {
        if (!drawer) throw new Error('Cart drawer is unavailable');
        drawer.persistUpsellDecision(drawer.lastKnownCart, this, desiredSelected);
        if (desiredSelected) drawer.saveNestedBinding(drawer.lastKnownCart, this, null);
        const result = await drawer.reconcileUpsells();
        const verifiedCart = result.cart || await drawer.readCart();
        const verifiedSelected = drawer.findCartUpsellItems(verifiedCart.items || [], identity).some((item) => (
          Number(item.variant_id || item.id) === identity.variantId
        ));
        if (verifiedSelected !== desiredSelected) throw new Error('Cart upsell state could not be verified');

        const current = drawer.findUpsell(identity);
        current?.setSelected(desiredSelected);
        if (current) drawer.persistUpsellDecision(verifiedCart, current, desiredSelected);
        if (verifiedCart.item_count !== undefined) drawer.classList.toggle('is-empty', verifiedCart.item_count === 0);
        // reconcileUpsells already published the verified cart once.
        return { changed: result.changed, cart: verifiedCart };
      } catch (error) {
        const current = drawer?.findUpsell(identity) || this;
        current.setSelected(wasSelected);
        current.dataset.selectionHydrated = String(wasHydrated);
        console.error(error);
        dispatch(current, 'cart:error', { message: error.message });
        return { changed: false, error };
      } finally {
        const current = drawer?.findUpsell(identity) || this;
        current.dataset.loading = 'false';
      }
    }
  }
  define('cart-drawer-upsell', CartDrawerUpsell);

  class ProductInfoUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.toggleButton = this.querySelector('.upsell-toggle-btn');
      this.variantInput = this.querySelector('product-form input[name="id"]');

      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant());
      });
      if (this.dataset.toggle === 'true' && this.toggleButton) {
        this.toggleButton.addEventListener('click', (event) => {
          if (event.target.closest('select, option, input, a, product-form')) return;
          event.preventDefault();
          this.setSelected(this.dataset.selected !== 'true');
        });
      }

      this.setSelected(this.dataset.selected === 'true');
      this.updateVariant();
      if (this.dataset.updatePrices === 'true') {
        this.priceObserver = new MutationObserver(() => {
          window.queueMicrotask(() => this.syncVisiblePrice());
        });
        this.priceObserver.observe(this, { childList: true, subtree: true });
      }
    }

    syncVisiblePrice() {
      if (this.dataset.updatePrices !== 'true') return;
      const price = Number(this.dataset.price || 0);
      const comparePrice = Number(this.dataset.comparePrice || 0);
      const moneyFormat = this.dataset.moneyFormat;
      const formattedPrice = formatMoney(price, moneyFormat);
      const formattedCompare = comparePrice > price ? formatMoney(comparePrice, moneyFormat) : '';
      this.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
        if (node.textContent !== formattedPrice) node.textContent = formattedPrice;
      });
      this.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
        if (node.textContent !== formattedCompare) node.textContent = formattedCompare;
        node.classList.toggle('hidden', comparePrice <= price);
      });
    }

    updateVariant() {
      if (!this.variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = findVariant(this.variants, selectedValues(picker));
      if (!variant) return;
      this.dataset.id = String(variant.id);
      this.dataset.variantAvailable = String(Boolean(variant.available));
      if (this.variantInput) this.variantInput.value = String(variant.id);
      const checkbox = this.querySelector('.bundle-deals__checkbox-js');
      if (checkbox) {
        const percentage = Number(this.dataset.percentageLeft || 1);
        const fixed = Number(this.dataset.fixedDiscount || 0);
        const price = Math.max(0, Number(variant.price || 0) * percentage - fixed);
        const comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
        checkbox.dataset.id = String(variant.id);
        checkbox.dataset.price = String(price);
        checkbox.dataset.comparePrice = String(comparePrice);
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
        if (this.dataset.updatePrices === 'true') {
          this.dataset.price = String(price);
          this.dataset.comparePrice = String(comparePrice);
          this.syncVisiblePrice();
        }
      }
      this.closest('main-bundle-offer')?.refresh?.();
    }

    setSelected(selected) {
      this.dataset.selected = String(selected);
      this.classList.toggle('upsell--selected', selected);
      this.toggleButton?.setAttribute('aria-pressed', String(selected));
    }

    disconnectedCallback() {
      this.priceObserver?.disconnect();
    }
  }
  define('product-info-upsell', ProductInfoUpsell);

  class MainBundleOffer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        if (event.target.matches('.bundle-deals__variant-selects-js select')) {
          this.updateItemVariant(event.target);
        }
        window.queueMicrotask(() => this.refresh());
      });
      this.refresh();
    }

    updateItemVariant(select) {
      const picker = select.closest('.bundle-deals__variant-selects-js');
      const item = picker?.closest('product-info-upsell');
      if (!picker || !item || item.dataset.updatePrices !== 'true') return;
      const variant = findVariant(parseJsonScript(picker), selectedValues(picker));
      if (!variant) return;
      const percentage = Number(item.dataset.percentageLeft || 1);
      const fixed = Number(item.dataset.fixedDiscount || 0);
      const price = Math.max(0, Number(variant.price || 0) * percentage - fixed);
      const comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
      const checkbox = item.querySelector('.bundle-deals__checkbox-js');

      item.dataset.id = String(variant.id);
      item.dataset.price = String(price);
      item.dataset.comparePrice = String(comparePrice);
      if (checkbox) {
        checkbox.dataset.id = String(variant.id);
        checkbox.dataset.price = String(price);
        checkbox.dataset.comparePrice = String(comparePrice);
        checkbox.disabled = !variant.available;
        if (!variant.available) checkbox.checked = false;
      }
      item.dataset.variantAvailable = String(Boolean(variant.available));

      // MainBundleOffer is the component that always receives the bubbling
      // picker change and recalculates the correct total. Update the visible
      // price in that same authoritative path instead of depending on the
      // child custom-element lifecycle used by a particular block renderer.
      const moneyFormat = item.dataset.moneyFormat || this.dataset.moneyFormat;
      item.querySelectorAll('.bundle-deals__price-js').forEach((node) => {
        node.textContent = formatMoney(price, moneyFormat);
      });
      item.querySelectorAll('.bundle-deals__compare-price-js').forEach((node) => {
        node.textContent = comparePrice > price ? formatMoney(comparePrice, moneyFormat) : '';
        node.classList.toggle('hidden', comparePrice <= price);
        node.hidden = comparePrice <= price;
      });
    }

    refresh() {
      const selected = Array.from(this.querySelectorAll('product-info-upsell[data-selected="true"]'))
        .filter((item) => !item.querySelector('.bundle-deals__checkbox-js')?.disabled);
      // The legacy bundle listener can refresh totals after ProductInfoUpsell
      // has already resolved the new variant while leaving its rendered line
      // price stale. Re-apply the authoritative item datasets at the final
      // Main-offer refresh boundary so execution order cannot split the two.
      selected.forEach((item) => {
        if (item.dataset.updatePrices !== 'true') return;
        if (typeof item.syncVisiblePrice === 'function') {
          item.syncVisiblePrice();
          return;
        }
        const itemPrice = Number(item.dataset.price || 0);
        const itemCompare = Number(item.dataset.comparePrice || 0);
        item.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
          node.textContent = formatMoney(itemPrice, item.dataset.moneyFormat || this.dataset.moneyFormat);
        });
        item.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
          node.textContent = itemCompare > itemPrice
            ? formatMoney(itemCompare, item.dataset.moneyFormat || this.dataset.moneyFormat)
            : '';
          node.classList.toggle('hidden', itemCompare <= itemPrice);
          node.hidden = itemCompare <= itemPrice;
        });
      });
      const percentage = Number(this.dataset.percentageLeft || 1);
      const fixed = Number(this.dataset.fixedDiscount || 0);
      const total = Math.max(0, selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0) * percentage - fixed);
      const compare = selected.reduce((sum, item) => sum + Number(item.dataset.comparePrice || 0), 0);
      const priceNode = this.querySelector('.bundle-deals__total-price-js');
      const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
      if (priceNode) priceNode.textContent = formatMoney(total, this.dataset.moneyFormat);
      if (compareNode) {
        compareNode.textContent = compare > total ? formatMoney(compare, this.dataset.moneyFormat) : '';
        compareNode.hidden = compare <= total;
      }
      dispatch(this, 'bundle:change', { items: selected.map((item) => Number(item.dataset.id)), total });
    }
  }
  define('main-bundle-offer', MainBundleOffer);

  class ComparisonSlider extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range || range.dataset.runtimeBound) return;
      range.dataset.runtimeBound = 'true';
      const update = () => this.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    }
  }
  define('comparison-slider', ComparisonSlider);

  class InternalVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.timeline = this.querySelector('.internal-video__timeline');
      this.querySelectorAll('[data-play], .internal-video__play, .play-btn').forEach((button) => button.addEventListener('click', () => this.toggle()));
      this.querySelectorAll('[data-sound], .internal-video__sound, .internal-video__sound-btn').forEach((button) => button.addEventListener('click', () => this.toggleSound()));
      this.querySelectorAll('[data-fullscreen], .internal-video__fullscreen-btn').forEach((button) => button.addEventListener('click', () => this.toggleFullscreen()));
      this.video.addEventListener('play', () => this.updatePlaybackState());
      this.video.addEventListener('pause', () => this.updatePlaybackState());
      this.video.addEventListener('ended', () => this.updatePlaybackState());
      this.video.addEventListener('waiting', () => this.classList.add('internal-video--loading'));
      this.video.addEventListener('playing', () => this.classList.remove('internal-video--loading'));
      this.video.addEventListener('timeupdate', () => this.updateTimeline());
      this.onFullscreenChange = () => this.classList.toggle('internal-video--fullscreen', document.fullscreenElement === this);
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      if (this.timeline) {
        this.timeline.setAttribute('role', 'slider');
        this.timeline.setAttribute('aria-valuemin', '0');
        this.timeline.setAttribute('aria-valuemax', '100');
        if (this.timeline.tabIndex < 0) this.timeline.tabIndex = 0;
        this.timeline.addEventListener('pointerdown', (event) => this.startTimelineDrag(event));
        this.timeline.addEventListener('keydown', (event) => this.onTimelineKeydown(event));
      }
      this.updatePlaybackState();
      this.updateSoundState();
      this.updateTimeline();
      if (this.dataset.autoplay === 'true') this.observe();
    }

    toggle() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    updatePlaybackState() {
      const playing = !this.video.paused && !this.video.ended;
      this.dataset.playing = String(playing);
      this.classList.toggle('internal-video--playing', playing);
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.updateSoundState();
    }

    updateSoundState() {
      const muted = this.video.muted;
      this.dataset.muted = String(muted);
      this.classList.toggle('internal-video--muted', muted);
      this.querySelectorAll('[data-sound], .internal-video__sound, .internal-video__sound-btn').forEach((button) => {
        button.setAttribute('aria-pressed', String(!muted));
      });
    }

    toggleFullscreen() {
      if (document.fullscreenElement === this) document.exitFullscreen?.();
      else this.requestFullscreen?.().catch?.(() => {});
    }

    updateTimeline() {
      if (!this.timeline) return;
      const percent = this.video.duration > 0 ? (this.video.currentTime / this.video.duration) * 100 : 0;
      this.timeline.style.setProperty('--completed', `${percent}%`);
      this.timeline.setAttribute('aria-valuenow', String(Math.round(percent)));
    }

    seekFromPointer(event) {
      if (!this.timeline || !(this.video.duration > 0)) return;
      const rect = this.timeline.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
      this.video.currentTime = ratio * this.video.duration;
      this.updateTimeline();
    }

    startTimelineDrag(event) {
      event.preventDefault();
      this.timeline.setPointerCapture?.(event.pointerId);
      this.seekFromPointer(event);
      const move = (moveEvent) => this.seekFromPointer(moveEvent);
      const stop = () => {
        this.timeline.removeEventListener('pointermove', move);
        this.timeline.removeEventListener('pointerup', stop);
        this.timeline.removeEventListener('pointercancel', stop);
      };
      this.timeline.addEventListener('pointermove', move);
      this.timeline.addEventListener('pointerup', stop);
      this.timeline.addEventListener('pointercancel', stop);
    }

    onTimelineKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !(this.video.duration > 0)) return;
      event.preventDefault();
      if (event.key === 'Home') this.video.currentTime = 0;
      else if (event.key === 'End') this.video.currentTime = this.video.duration;
      else this.video.currentTime = Math.max(0, Math.min(this.video.duration, this.video.currentTime + (event.key === 'ArrowRight' ? 5 : -5)));
      this.updateTimeline();
    }

    observe() {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.video.play().catch(() => {});
        else this.video.pause();
      }, { threshold: 0.35 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    }
  }
  define('internal-video', InternalVideo);

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.dayLabels || '').split(',').map((value) => value.trim());
      const months = (this.dataset.monthLabels || '').split(',').map((value) => value.trim());
      const today = new Date();
      this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
        const normalize = (value) => Number.isFinite(Number(value)) ? Math.min(3650, Math.max(0, Math.floor(Number(value)))) : 0;
        const min = normalize(element.dataset.minDays);
        const max = Math.max(min, normalize(element.dataset.maxDays ?? min));
        const start = this.addDays(min, today);
        const end = this.addDays(max, today);
        element.hidden = !start || !end;
        if (element.hidden) return;
        const template = element.dataset.text || element.textContent;
        element.textContent = template
          .replace(/\[start_date\]/g, this.format(start, days, months))
          .replace(/\[end_date\]/g, this.format(end, days, months));
      });
    }

    parseSkippedDays(value) {
      const aliases = {
        sun: 0, sunday: 0, '周日': 0, '星期日': 0, '星期天': 0,
        mon: 1, monday: 1, '周一': 1, '星期一': 1,
        tue: 2, tues: 2, tuesday: 2, '周二': 2, '星期二': 2,
        wed: 3, wednesday: 3, '周三': 3, '星期三': 3,
        thu: 4, thur: 4, thurs: 4, thursday: 4, '周四': 4, '星期四': 4,
        fri: 5, friday: 5, '周五': 5, '星期五': 5,
        sat: 6, saturday: 6, '周六': 6, '星期六': 6,
      };
      return new Set(String(value).split(',').map((part) => {
        const normalized = part.trim().toLocaleLowerCase();
        if (/^[0-6]$/.test(normalized)) return Number(normalized);
        return aliases[normalized];
      }).filter((day) => day !== undefined));
    }

    parseSkippedDates(value) {
      return String(value).split(',').map((part) => part.trim()).filter(Boolean).map((part) => {
        const match = part.match(/^(?:(\d{4})[-/.])?(\d{1,2})[-/.](\d{1,2})$/);
        return match ? { year: Number(match[1]) || null, month: Number(match[2]), day: Number(match[3]) } : null;
      }).filter(Boolean);
    }

    isSkipped(date) {
      if (this.skippedDays.has(date.getDay())) return true;
      return this.skippedDates.some((item) => (
        (!item.year || item.year === date.getFullYear())
        && item.month === date.getMonth() + 1
        && item.day === date.getDate()
      ));
    }

    addDays(amount, today = new Date()) {
      const value = new Date(today);
      value.setHours(12, 0, 0, 0);
      const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const parse = (input) => new Set((input || '').toLowerCase().split(',').map((item) => item.trim()).filter(Boolean));
      const skipDays = parse(this.dataset.daysToSkip);
      const skipDates = parse(this.dataset.datesToSkip);
      this.skippedDays = this.parseSkippedDays(this.dataset.daysToSkip || '');
      this.skippedDates = this.parseSkippedDates(this.dataset.datesToSkip || '');
      const excluded = () => this.isSkipped(value);
      let remaining = amount;
      for (let guard = 0; guard < 36500; guard += 1) {
        if (remaining === 0 && !excluded()) return value;
        value.setDate(value.getDate() + 1);
        if (!excluded() && remaining > 0) remaining -= 1;
      }
      return null;
    }

    format(date, days, months) {
      const format = this.dataset.dateFormat || 'mm_dd';
      const month = months[date.getMonth()] || date.toLocaleString(document.documentElement.lang || 'en', { month: 'short' });
      const day = days[(date.getDay() + 6) % 7] || date.toLocaleString(document.documentElement.lang || 'en', { weekday: 'short' });
      const number = date.getDate();
      const suffix = number % 100 >= 11 && number % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[number % 10] || 'th');
      const numeric = `${String(number).padStart(2, '0')}. ${String(date.getMonth() + 1).padStart(2, '0')}.`;
      const formats = {
        day_mm_dd: `${day}, ${month} ${number}${suffix}`,
        day_dd_mm: `${day}, ${number}. ${month}`,
        mm_dd: `${month} ${number}${suffix}`,
        dd_mm: `${number}. ${month}`,
        dd_mm_no_dot: `${number} ${month}`,
        day_dd_mm_numeric: `${day}, ${numeric}`,
        dd_mm_numeric: numeric,
      };
      return formats[format] || formats.mm_dd;
    }
  }
  define('dynamic-dates', DynamicDates);

  class ClickableDiscount extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('.clickable-discount__btn');
      if (!button || button.dataset.runtimeBound) return;
      button.dataset.runtimeBound = 'true';
      button.addEventListener('click', async () => {
        this.dataset.loading = 'true';
        try {
          const code = String(this.dataset.code || '').trim();
          if (!code) throw new Error('Discount code is empty');
          const task = () => postJson(window.routes?.cart_update_url || '/cart/update.js', { discount: code });
          const drawer = document.querySelector('cart-drawer');
          const response = await (drawer?.enqueueCartMutation ? drawer.enqueueCartMutation(task) : task());
          const cart = await parseJsonResponse(response, 'Discount could not be applied');
          const applied = (cart.discount_codes || []).some((discount) => (
            String(discount.code || '').toLocaleLowerCase() === code.toLocaleLowerCase()
            && discount.applicable !== false
          ));
          if (!response.ok || cart.status || !applied) throw new Error(cart.description || cart.message || 'Discount could not be applied');
          this.dataset.applied = 'true';
          this.dataset.error = 'false';
          button.setAttribute('aria-pressed', 'true');
          dispatch(this, 'cart:updated', { cart, source: 'clickable-discount' });
        } catch (_error) {
          this.dataset.error = 'true';
          this.dataset.applied = 'false';
        } finally {
          this.dataset.loading = 'false';
        }
      });
    }
  }
  define('clickable-discount', ClickableDiscount);

  class CustomProductField extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const productInfo = this.closest('product-info');
      const section = this.dataset.section || productInfo?.dataset.section || '';
      const escapedSection = window.CSS?.escape ? CSS.escape(String(section)) : String(section);
      const form = productInfo?.querySelector(`#product-form-${escapedSection}`)
        || productInfo?.querySelector('form[data-type="add-to-cart-form"]')
        || this.closest('form');
      const controls = Array.from(this.querySelectorAll('input:not([type="hidden"]), textarea, select'));
      const control = controls[0];
      if (!form || !control || !this.dataset.name) return;
      if (!form.getAttribute('id')) form.setAttribute('id', `product-form-${section || Math.random().toString(36).slice(2)}`);
      const propertyName = `properties[${this.dataset.name}]`;
      controls.forEach((item) => {
        item.name = propertyName;
        item.setAttribute('form', form.getAttribute('id'));
      });
      const required = this.dataset.required === 'true' || control.required;
      controls.forEach((item) => { item.required = required; });
      this.validate = (report = false) => {
        const radios = this.querySelectorAll('input[type="radio"]');
        const checked = this.querySelector('input[type="radio"]:checked');
        const value = radios.length ? (checked?.value || '') : (control.value || '');
        controls.forEach((item) => item.setCustomValidity(''));
        const missingRequired = required && !String(value).trim();
        const message = missingRequired
          ? (this.dataset.atcErrorMsg || this.querySelector('.input-error-msg')?.textContent?.trim() || 'Please complete this field.')
          : '';
        control.setCustomValidity(message);
        const invalid = missingRequired || !control.checkValidity();
        control.setAttribute('aria-invalid', String(invalid));
        this.classList.toggle('field-error', invalid);
        if (report && invalid) {
          control.reportValidity();
          control.focus?.();
        }
        return !invalid;
      };
      this.addEventListener('change', () => this.validate(false));
      this.addEventListener('input', () => this.validate(false));
      this.validate(false);
    }
  }
  define('custom-product-field', CustomProductField);

  class CardVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.variants = parseJsonScript(this);
      this.addEventListener('change', () => this.update());
      this.update();
    }

    update() {
      const values = Array.from(this.querySelectorAll('select, fieldset')).map((group) =>
        group.matches('select') ? group.value : group.querySelector('input:checked')?.value
      ).filter((value) => value !== undefined);
      const variant = findVariant(this.variants, values);
      const formId = this.getAttribute('if') || this.dataset.section;
      const form = document.getElementById(formId) || document.getElementById(`${formId}-submit`)?.form;
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
      const card = this.closest('.card-wrapper, .card');
      const button = card?.querySelector('[type="submit"]');
      if (button) button.disabled = !variant?.available;
      const image = card?.querySelector('.card__media img, .media img');
      const source = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src;
      if (image && source) image.src = source;
      dispatch(this, 'variant:change', { variant });
    }
  }
  define('card-variant-selects', CardVariantSelects);

  class QuantityBreakUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.querySelectorAll('[data-toggle="true"]').forEach((item) => item.addEventListener('click', (event) => {
        if (event.target.closest('select, a, button')) return;
        item.dataset.selected = String(item.dataset.selected !== 'true');
        dispatch(this, 'upsell:change', { item, selected: item.dataset.selected === 'true' });
      }));
    }
  }
  define('quantity-break-upsells', QuantityBreakUpsells);

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        const quantity = event.target.closest('input[name="quantity"]');
        if (quantity) this.applyQuantity(quantity);
        const select = event.target.closest('select');
        if (select) this.updateOfferVariant(select.closest('[data-index], .quantity-break'));
      });
      const selected = this.querySelector('input[name="quantity"]:checked');
      if (selected) this.applyQuantity(selected);
    }

    applyQuantity(input) {
      const amount = Number(input.dataset.quantity || input.value || 1);
      const productInfo = this.closest('product-info');
      const escapedSection = window.CSS?.escape
        ? CSS.escape(String(this.dataset.section || ''))
        : String(this.dataset.section || '');
      const field = productInfo?.querySelector(
        `#Quantity-${escapedSection}, #quantity-${escapedSection}, .quantity__input[name="quantity"]`
      );
      if (field && !this.contains(field) && Number(field.value) !== amount) {
        field.value = amount;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.querySelectorAll('quantity-break-upsells').forEach((upsell) => {
        upsell.hidden = Number(upsell.dataset.offer) !== Number(input.dataset.index || input.value || 1);
      });
      dispatch(this, 'quantity-break:change', { quantity: amount, input, section: this.dataset.section || '' });
    }

    updateOfferVariant(container) {
      if (!container) return;
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      if (!variant) return;
      container.dataset.selectedId = variant.id;
      container.querySelectorAll('[data-product], input[name="id"]').forEach((element) => {
        if (element.matches('input')) element.value = variant.id;
        else element.dataset.product = variant.id;
      });
    }
  }
  define('quantity-breaks', QuantityBreaks);

  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      const escapedSection = window.CSS?.escape
        ? CSS.escape(String(this.dataset.section || ''))
        : String(this.dataset.section || '');
      const productInfo = this.closest('product-info');
      this.quantityInput = productInfo?.querySelector(
        `#Quantity-${escapedSection}, #quantity-${escapedSection}, .quantity__input[name="quantity"]`
      );
      this.quantityInput?.addEventListener('change', () => this.update());
      this.onQuantityBreakChange = (event) => {
        if (String(event.detail?.section || '') !== String(this.dataset.section || '')) return;
        this.update(event.detail.quantity);
      };
      document.addEventListener('quantity-break:change', this.onQuantityBreakChange);
      this.update();
    }

    update(quantity = Number(this.quantityInput?.value || 1)) {
      this.querySelectorAll('.quantity-gift').forEach((gift) => {
        const unlocked = quantity >= Number(gift.dataset.quantity || 1);
        gift.dataset.unlocked = String(unlocked);
        gift.classList.toggle('quantity-gift--unlocked', unlocked);
        const form = gift.querySelector('product-form, form');
        if (form) form.dataset.selected = String(unlocked);
      });
      dispatch(this, 'quantity-gifts:change', { quantity });
    }

    disconnectedCallback() {
      document.removeEventListener('quantity-break:change', this.onQuantityBreakChange);
    }
  }
  define('quantity-gifts', QuantityGifts);

  class SubscriptionWidget extends HTMLElement {
    selectedPlanControl() {
      return this.querySelector('select option:checked, input[data-selling-plan-id]:checked') || this.querySelector('[data-selling-plan-id]') || {
        value: this.dataset.firstSellingPlanId || '',
        dataset: {
          sellingPlanId: this.dataset.firstSellingPlanId || '',
          price: this.dataset.firstPlanPrice,
          comparePrice: this.dataset.firstPlanComparePrice,
          discountType: this.dataset.discountType,
          discountValue: this.dataset.discountValue,
        },
      };
    }

    beginVariantUpdate() {
      this.dataset.variantPending = 'true';
      this.setAttribute('aria-busy', 'true');
      if (this.hiddenInput) this.hiddenInput.disabled = true;
    }

    refreshFromProduct(html, variant, sourceSection) {
      const source = html.getElementById(`SubscriptionWidget-${sourceSection}`);
      if (!source || String(source.dataset.variantId) !== String(variant.id)) {
        this.beginVariantUpdate();
        throw new Error('Subscription data does not match the selected variant');
      }
      const section = this.dataset.section;
      const previousMode = this.querySelector('.subscription__box[data-selected="true"]')?.dataset.type;
      const previousPlan = String(this.selectedPlanControl()?.value || '');
      // Refresh the server-calculated allocations and all editable price text,
      // retaining this instance's IDs and the shopper's still-valid choice.
      Object.assign(this.dataset, source.dataset, { section, variantPending: 'false' });
      this.innerHTML = source.innerHTML.split(sourceSection).join(section);
      this.planNotice = null;
      this.removeAttribute('aria-busy');
      this.hidden = !this.dataset.firstSellingPlanId;
      this.classList.toggle('hidden', this.hidden);
      if (this.hidden) {
        this.setPlanInput('', false);
        return this.dataset.requiresPlan !== 'true';
      }
      const plans = Array.from(this.querySelectorAll('[data-selling-plan-id]'));
      const retained = plans.find((plan) => String(plan.value) === previousPlan) || plans[0];
      plans.forEach((plan) => {
        if (plan.tagName === 'OPTION') plan.selected = plan === retained;
        else if (plan.type === 'radio') plan.checked = plan === retained;
      });
      this.updateSubscriptionPrices(this.selectedPlanControl());
      const mode = this.dataset.requiresPlan === 'true' ? 'subscription' : previousMode;
      const box = Array.from(this.querySelectorAll('.subscription__box')).find((item) => item.dataset.type === mode)
        || this.querySelector('.subscription__box[data-selected="true"]') || this.querySelector('.subscription__box');
      if (box) this.selectMode(box);
      return true;
    }

    connectedCallback() {
      if (this.onModeClick) return;
      this.onModeClick = (event) => {
        const box = event.target.closest('.subscription__box');
        if (box) this.selectMode(box);
      };
      this.onPlanChange = (event) => {
        if (event.target.matches('[data-selling-plan-id], #SubscriptionFrequency-' + this.dataset.section)) this.updatePlan(event.target);
      };
      this.addEventListener('click', this.onModeClick);
      this.addEventListener('change', this.onPlanChange);
      const selected = this.querySelector('.subscription__box[data-selected="true"]') || this.querySelector('.subscription__box');
      if (selected) this.selectMode(selected);
      const productRoot = this.closest('product-info') || this.parentElement;
      if (productRoot) {
        this.formObserver = new MutationObserver(() => this.syncPlanInput());
        this.formObserver.observe(productRoot, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      this.formObserver?.disconnect();
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.removeEventListener('click', this.onModeClick);
      this.removeEventListener('change', this.onPlanChange);
      this.hiddenInput?.remove();
      this.hiddenInput = null;
      this.form = null;
      this.onModeClick = null;
      this.onPlanChange = null;
    }

    ensurePlanInput() {
      // Never adopt a same-name field: an app may own it, even while disabled.
      const form = document.getElementById(`product-form-${this.dataset.section}`)
        || this.closest('product-info')?.querySelector('product-form form[action*="/cart/add"]');
      if (form !== this.form) {
        this.form?.removeEventListener('submit', this.onFormSubmit, true);
        this.hiddenInput?.remove();
        this.hiddenInput = null;
        this.form = form;
        this.onFormSubmit ||= () => this.syncPlanInput();
        this.form?.addEventListener('submit', this.onFormSubmit, true);
      }
      if (!this.form) return false;
      const fields = Array.from(this.form.querySelectorAll('[name="selling_plan"]'));
      Array.from(this.form.elements || []).forEach((field) => {
        if (field.name === 'selling_plan' && !fields.includes(field)) fields.push(field);
      });
      if (fields.some((field) => field !== this.hiddenInput)) {
        if (this.hiddenInput) this.hiddenInput.disabled = true;
        this.dataset.planManaged = 'external';
        this.setPlanNotice(true);
        return false;
      }
      this.dataset.planManaged = 'theme';
      this.setPlanNotice(false);
      if (!this.hiddenInput || this.hiddenInput.parentNode !== this.form) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = 'selling_plan';
        this.hiddenInput.dataset.generalStorePlan = 'true';
        this.hiddenInput.disabled = true;
        this.form.append(this.hiddenInput);
      }
      return true;
    }

    setPlanNotice(visible) {
      if (!visible) {
        this.planNotice?.remove();
        this.planNotice = null;
        return;
      }
      if (this.planNotice) return;
      this.planNotice = document.createElement('p');
      this.planNotice.setAttribute('role', 'status');
      this.planNotice.textContent = 'Purchase options are managed by the subscription app. Please use its selector.';
      this.append(this.planNotice);
    }

    selectMode(box) {
      if (this.dataset.variantPending === 'true') return;
      if (this.dataset.requiresPlan === 'true' && box.dataset.type !== 'subscription') {
        box = this.querySelector('.subscription__box--subscription');
      }
      if (!box) return;
      if (!this.ensurePlanInput()) return;
      this.querySelectorAll('.subscription__box').forEach((item) => {
        const active = item === box;
        item.dataset.selected = String(active);
        item.classList.toggle('subscription__box--selected', active);
      });
      const subscription = box.dataset.type === 'subscription';
      const selector = this.selectedPlanControl();
      const sellingPlanId = subscription ? (selector?.dataset.sellingPlanId || selector?.value || this.dataset.firstSellingPlanId || '') : '';
      this.setPlanInput(sellingPlanId, subscription);
      if (subscription && selector) {
        this.updateSubscriptionPrices(selector);
        this.updateMainProductPrice(Number(selector.dataset.price || 0));
      } else {
        this.updateMainProductPrice(Number(this.dataset.basePrice || 0));
      }
    }

    updatePlan(control) {
      if (this.dataset.variantPending === 'true') return;
      if (!this.ensurePlanInput()) return;
      const selected = control.matches('select') ? control.selectedOptions[0] : control;
      const sellingPlanId = selected?.dataset.sellingPlanId || selected?.value || '';
      this.setPlanInput(sellingPlanId, true);
      this.updateSubscriptionPrices(selected);
      this.updateMainProductPrice(Number(selected?.dataset.price || 0));
    }

    setPlanInput(sellingPlanId, subscription) {
      if (!this.ensurePlanInput()) return false;
      this.hiddenInput.disabled = !subscription || !sellingPlanId;
      this.hiddenInput.value = subscription ? sellingPlanId : '';
      this.hiddenInput.setAttribute('value', this.hiddenInput.value);
      return true;
    }

    updateSubscriptionPrices(selected) {
      if (!selected) return;
      const price = Number(selected.dataset.price || 0);
      let comparePrice = Number(selected.dataset.comparePrice ?? price);
      if (this.dataset.subCompareBasedOn === 'compare') comparePrice = Math.max(comparePrice, Number(this.dataset.baseComparePrice || 0));
      const amountSaved = Math.max(comparePrice - price, 0);
      const percentageSaved = comparePrice > 0 ? Math.floor(amountSaved * 100 / comparePrice) : 0;
      const values = {
        '[price]': formatMoney(price, this.dataset.moneyFormat),
        '[compare_price]': formatMoney(comparePrice, this.dataset.moneyFormat),
        '[amount_saved]': formatMoney(amountSaved, this.dataset.moneyFormat),
        '[amount_saved_rounded]': formatMoney(Math.round(amountSaved / 100) * 100, this.dataset.moneyFormat),
        '[percentage_saved]': `${percentageSaved}%`,
        '[subscription_name]': this.dataset.subscriptionName || '',
      };
      this.querySelectorAll('.sub-price-update[data-text]').forEach((element) => {
        let html = element.dataset.text || '';
        Object.entries(values).forEach(([token, value]) => { html = html.split(token).join(value); });
        element.innerHTML = html;
      });
    }

    updateMainProductPrice(price) {
      if (this.dataset.updatePrices === 'false') return;
      const priceRoot = document.getElementById(`price-${this.dataset.section}`);
      if (!priceRoot || !Number.isFinite(price) || price < 0) return;
      const comparePrice = Number(this.dataset.baseComparePrice || price);
      const productInfo = this.closest('product-info');
      if (typeof productInfo?.updateVisiblePrice === 'function') {
        const available = !priceRoot.querySelector('.price')?.classList.contains('price--sold-out');
        productInfo.updateVisiblePrice({ price, compare_at_price: comparePrice, available });
        return;
      }
      const formattedPrice = formatMoney(price, this.dataset.moneyFormat);
      priceRoot.querySelectorAll('.main-price').forEach((element) => { element.textContent = formattedPrice; });
      const amountSaved = Math.max(comparePrice - price, 0);
      const percentageSaved = comparePrice > 0 ? Math.floor(amountSaved * 100 / comparePrice) : 0;
      priceRoot.querySelectorAll('.badge__percentage-saved').forEach((element) => { element.textContent = String(percentageSaved); });
      priceRoot.querySelectorAll('.badge__money-saved').forEach((element) => {
        element.textContent = formatMoney(amountSaved, this.dataset.moneyFormat).replace(/\.00(?=\D*$)/, '');
      });
    }

    syncPlanInput() {
      if (this.dataset.variantPending === 'true') return;
      const subscription = this.querySelector('.subscription__box--subscription')?.dataset.selected === 'true';
      const selected = this.selectedPlanControl();
      this.setPlanInput(subscription ? (selected?.dataset.sellingPlanId || selected?.value || this.dataset.firstSellingPlanId || '') : '', subscription);
    }
  }
  define('subscription-widget', SubscriptionWidget);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) { this.startVisibilityTracking(); return; }
      this.dataset.runtimeBound = 'true';
      this.button = this.querySelector('.sticky-atc__add_button');
      this.resolveProductForm();
      this.querySelector('.sticky-atc__scroll-btn')?.addEventListener('click', event => this.scrollToDestination(event));
      this.button?.addEventListener('click', (event) => {
        this.mainButton = this.resolveMainButton();
        window.CartDebug?.log('sticky.forward', { section: this.dataset.section, buttonId: this.mainButton?.id, disabled: this.mainButton?.disabled, connected: this.mainButton?.isConnected });
        if (this.dataset.scrollBtn === 'true') return this.scrollToDestination(event);
        if (this.mainButton && this.button.type === 'button') {
          event.preventDefault();
          this.mainButton.click();
        }
      });
      this.addEventListener('change', (event) => this.syncVariant(event.target));
      this.startVisibilityTracking();
    }

    resolveProductForm() {
      this.mainButton = this.resolveMainButton();
      this.form = this.mainButton?.form;
      this.productForm = this.mainButton?.closest('product-form');
    }

    resolveMainButton() {
      const associated = this.button?.form?.querySelector('[name="add"][type="submit"]');
      if (associated) return associated;
      if (this.dataset.section) return document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      return document.querySelector('product-form[data-main="true"] [name="add"][type="submit"]');
    }

    startVisibilityTracking() {
      if (!this.isConnected) return;
      this.variantUnsubscriber?.();
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data;
          if (String(data?.sectionId) === String(this.dataset.section)) this.syncFromMainVariant(data.variant, data.html);
        });
      }

      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      this.visibilityObserver?.disconnect();
      this.onScroll = () => {
        this.button = this.querySelector('.sticky-atc__add_button');
        this.mainButton = this.resolveMainButton();
        const afterScroll = this.dataset.afterScroll === 'true';
        const visible = !afterScroll || Boolean(this.mainButton && this.mainButton.getBoundingClientRect().bottom <= 0);
        this.classList.toggle('active', visible);
        this.removeAttribute('hidden');
        this.setAttribute('aria-hidden', String(!visible));
        this.inert = !visible;
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      // Editor section rendering can connect the sticky block before the purchase form.
      this.visibilityObserver = new MutationObserver(() => this.onScroll());
      this.visibilityObserver.observe(this.closest('product-info') || this.parentElement, { childList: true, subtree: true });
      this.onScroll();
      queueMicrotask(() => { if (this.isConnected) this.onScroll(); });
    }

    syncVariant(control) {
      if (!control.matches('select')) return;
      this.resolveProductForm();
      const picker = control.closest('[id^="StickyAtcVariantPicker-"]') || this;
      const variants = parseJsonScript(picker);
      const combinedId = control.selectedOptions[0]?.dataset.variantId;
      const variant = combinedId
        ? variants.find((item) => String(item.id) === String(combinedId))
        : findVariant(variants, selectedValues(picker || this));
      if (!variant) return;
      const mainId = this.form?.querySelector('input[name="id"]');
      if (mainId) {
        mainId.value = variant.id;
        mainId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.form) this.form.dataset.submitVariantId = String(variant.id);
      if (this.button) this.button.disabled = !variant.available;
      this.syncMainVariant(variant);
      dispatch(this, 'variant:change', { variant });
    }

    scrollToDestination(event) {
      event.preventDefault();
      let selector = String(this.dataset.scrollDestination || '').trim();
      if (!selector) return;
      selector = selector.replace(/-id\b/g, `-${this.dataset.section}`);
      let target = document.getElementById(selector.replace(/^#/, ''));
      if (!target) {
        try { target = document.querySelector(selector); } catch (_error) { target = null; }
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    syncMainVariant(variant) {
      const productInfo = this.productForm?.closest('product-info')
        || document.getElementById(`ProductInfo-${this.dataset.section}`);
      const picker = productInfo?.querySelector('variant-selects');
      const options = Array.isArray(variant?.options)
        ? variant.options
        : [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
      if (!picker || !options.length) return false;

      let changedControl = null;
      picker.querySelectorAll('.product-form__input').forEach((group, index) => {
        const expected = String(options[index] ?? '');
        if (!expected) return;

        const radio = Array.from(group.querySelectorAll('input[type="radio"]'))
          .find((input) => String(input.value) === expected);
        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            changedControl ||= radio;
          }
          return;
        }

        const select = group.querySelector('select');
        if (!select) return;
        const option = Array.from(select.options)
          .find((item) => String(item.value) === expected);
        if (option && String(select.value) !== expected) {
          select.value = option.value;
          changedControl ||= select;
        }
      });

      if (!changedControl) return false;
      changedControl.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    syncFromMainVariant(variant, html) {
      if (!variant) return;
      this.resolveProductForm();
      const picker = this.querySelector('[id^="StickyAtcVariantPicker-"]');
      if (picker) {
        const combined = picker.querySelector('.sticky-atc__variant-select');
        if (combined) {
          const option = Array.from(combined.options).find(
            (item) => String(item.dataset.variantId) === String(variant.id)
          );
          if (option) combined.value = option.value;
        } else {
          Array.from(picker.querySelectorAll('select')).forEach((select, index) => {
            if (variant.options?.[index] != null) select.value = variant.options[index];
          });
        }
      }

      if (this.form) delete this.form.dataset.submitVariantId;
      if (this.button) this.button.disabled = !variant.available;

      const source = html?.querySelector?.(`#sticky-atc-${this.dataset.section}`);
      const selectors = [
        '.sticky-atc__price',
        '.sticky-atc__button-price',
        '.sticky-atc__label__text',
      ];
      if (source) {
        selectors.forEach((selector) => {
          const destination = this.querySelector(selector);
          const replacement = source.querySelector(selector);
          if (destination && replacement) destination.innerHTML = replacement.innerHTML;
        });
        const destinationImage = this.querySelector('.sticky-atc__image img');
        const sourceImage = source.querySelector('.sticky-atc__image img');
        if (destinationImage && sourceImage) {
          destinationImage.src = sourceImage.src;
          destinationImage.alt = sourceImage.alt;
        }
      }

      // The section response may not contain the sticky block (for example when
      // Shopify returns only the product-info section). Keep the sticky price in
      // sync from the same variant payload instead of leaving the original price.
      this.syncPriceFromVariant(variant);
    }

    syncPriceFromVariant(variant) {
      const price = Number(variant?.price);
      const comparePrice = Number(variant?.compare_at_price || price);
      if (!Number.isFinite(price) || !Number.isFinite(comparePrice)) return;

      const moneyFormat = window.Shopify?.money_format || '${{amount}}';
      const money = (value) => formatMoney(value, moneyFormat);
      const onSale = comparePrice > price;
      const priceRoot = this.querySelector('.sticky-atc__price .price');

      if (priceRoot) {
        priceRoot.classList.toggle('price--on-sale', onSale);
        priceRoot.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
          .forEach((node) => { node.textContent = money(price); });
        priceRoot.querySelectorAll('.price__compare-price .price-item--regular')
          .forEach((node) => { node.textContent = money(comparePrice); });
        priceRoot.querySelectorAll('.badge__percentage-saved')
          .forEach((node) => {
            node.textContent = onSale ? String(Math.floor(((comparePrice - price) * 100) / comparePrice)) : '0';
          });
        priceRoot.querySelectorAll('.badge__money-saved')
          .forEach((node) => { node.textContent = money(onSale ? comparePrice - price : 0); });
      }

      this.querySelectorAll(`.main-atc-price-span-${this.dataset.section}`)
        .forEach((node) => { node.textContent = money(price); });
    }

    disconnectedCallback() {
      this.variantUnsubscriber?.();
      this.visibilityObserver?.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }
  }
  define('sticky-atc', StickyAtc);

  class BundleDeals extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.addEventListener('change', (event) => {
        if (event.target.matches('.bundle-deals__checkbox-js')) event.target.dataset.checked = String(event.target.checked);
        if (event.target.matches('.bundle-deals__variant-selects-js select')) this.updateVariant(event.target.closest('.bundle-deals__variant-selects-js'));
        this.refresh();
      });
      this.refresh();
    }

    updateVariant(container) {
      const variants = parseJsonScript(container);
      const variant = findVariant(variants, selectedValues(container));
      const index = container.dataset.index;
      const checkbox = this.querySelector(`.bundle-deals__checkbox-js[data-index="${index}"]`);
      if (!checkbox) return;
      const item = container.closest('product-info-upsell, .upsell');
      if (!variant) {
        checkbox.dataset.id = '';
        checkbox.disabled = true;
        checkbox.checked = false;
        if (item) {
          item.dataset.id = '';
          item.dataset.variantAvailable = 'false';
        }
        return;
      }
      const percentage = Number(item?.dataset.percentageLeft || container.dataset.percentageLeft || 1);
      const fixed = Number(item?.dataset.fixedDiscount || container.dataset.fixedDiscount || 0);
      checkbox.dataset.id = variant.id;
      checkbox.dataset.price = Math.max(0, Number(variant.price) * percentage - fixed);
      checkbox.dataset.comparePrice = Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0));
      checkbox.disabled = !variant.available;
      if (!variant.available) checkbox.checked = false;
      else if (checkbox.dataset.checked === 'true') checkbox.checked = true;
      if (item) item.dataset.variantAvailable = String(Boolean(variant.available));

      // Main offers use the shared bundle-deals change path as well. Keep the
      // visible line-item price in sync here, including legacy markup where
      // the item wrapper is not upgraded to product-info-upsell.
      if (item?.dataset.updatePrices === 'true') {
        item.dataset.id = String(variant.id);
        item.dataset.price = checkbox.dataset.price;
        item.dataset.comparePrice = checkbox.dataset.comparePrice;
        item.querySelectorAll('.bundle-deals__price-js, .regular-price').forEach((node) => {
          node.textContent = formatMoney(Number(checkbox.dataset.price || 0), item.dataset.moneyFormat || this.dataset.moneyFormat);
        });
        item.querySelectorAll('.bundle-deals__compare-price-js, .compare-price').forEach((node) => {
          const compare = Number(checkbox.dataset.comparePrice || 0);
          const price = Number(checkbox.dataset.price || 0);
          node.textContent = compare > price ? formatMoney(compare, item.dataset.moneyFormat || this.dataset.moneyFormat) : '';
          node.classList.toggle('hidden', compare <= price);
          node.hidden = compare <= price;
        });
      }
    }

    resolveVariantsHost() {
      const localHost = this.querySelector('.product-form__variants');
      if (localHost) return localHost;

      const siblingForm = this.nextElementSibling;
      if (
        siblingForm?.matches('product-form') &&
        (!this.dataset.section || siblingForm.dataset.section === this.dataset.section)
      ) {
        return siblingForm.querySelector('.product-form__variants');
      }

      const bundle = this.closest('.bundle-offer');
      const matchingForm = Array.from(bundle?.querySelectorAll('product-form') || []).find(
        (form) => !this.dataset.section || form.dataset.section === this.dataset.section,
      );
      return matchingForm?.querySelector('.product-form__variants') || null;
    }

    refresh() {
      const selected = Array.from(this.querySelectorAll('.bundle-deals__checkbox-js:checked:not(:disabled)'));
      const percentage = Number(this.dataset.percentageLeft || 1);
      const fixed = Number(this.dataset.fixedDiscount || 0);
      const total = Math.max(0, selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0) * percentage - fixed);
      const compare = selected.reduce((sum, item) => sum + Number(item.dataset.comparePrice || 0), 0);
      const priceNode = this.querySelector('.bundle-deals__total-price-js');
      const compareNode = this.querySelector('.bundle-deals__total-compare-price-js');
      if (priceNode) priceNode.textContent = formatMoney(total);
      if (compareNode) {
        compareNode.textContent = compare > total ? formatMoney(compare) : '';
        compareNode.hidden = compare <= total;
      }
      const variantsHost = this.resolveVariantsHost();
      if (variantsHost) {
        variantsHost.innerHTML = '';
        selected.forEach((item) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'items[][id]';
          input.value = item.dataset.id;
          variantsHost.append(input);
          const quantity = document.createElement('input');
          quantity.type = 'hidden';
          quantity.name = 'items[][quantity]';
          quantity.value = '1';
          variantsHost.append(quantity);
        });
      }
      const submit = this.querySelector('[type="submit"]');
      if (submit) submit.disabled = selected.length === 0;
      dispatch(this, 'bundle:change', { items: selected.map((item) => Number(item.dataset.id)), total });
    }
  }
  define('bundle-deals', BundleDeals);

  class CartDrawerGift extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.onCartUpdated = (event) => this.update(event.detail.cart);
      document.addEventListener('cart:updated', this.onCartUpdated);
      this.querySelectorAll('.upsell__variant-picker select').forEach((select) => {
        select.addEventListener('change', () => this.updateVariant());
      });
      this.updateVariant();
      this.update();
    }

    updateVariant() {
      const variants = parseJsonScript(this);
      if (!variants.length) return;
      const picker = this.querySelector('.upsell__variant-picker') || this;
      const variant = resolveSelectableVariant(picker, variants, {
        skipUnavailable: true,
        skipNonExistent: true,
      });
      if (!variant || !variant.available) {
        this.dataset.variantAvailable = 'false';
        return;
      }
      this.dataset.variantAvailable = 'true';
      this.dataset.id = String(variant.id);
      const input = this.querySelector('input[name="id"]');
      if (input) input.value = String(variant.id);
      this.update();
    }

    setUnlocked(unlocked) {
      this.dataset.selected = String(unlocked);
      this.classList.toggle('is-unlocked', unlocked);
    }

    update(cart = null) {
      if (this.updatePromise) return this.updatePromise;
      const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
      const task = async () => {
        try {
          return await this.reconcileGift(cart);
        } catch (error) {
          console.error(error);
          dispatch(this, 'cart:error', { message: error.message });
          return { changed: false, cart: null, error };
        }
      };
      this.updatePromise = window.Shopify?.designMode || !drawer?.enqueueCartMutation
        ? task()
        : drawer.enqueueCartMutation(task);
      const clear = () => { this.updatePromise = null; };
      this.updatePromise.then(clear, clear);
      return this.updatePromise;
    }

    getGiftItems(cart) {
      const key = String(this.dataset.giftKey || '');
      if (!key) return [];
      return (cart?.items || []).filter((item) => itemProperty(item, '_theme_gift_key') === key);
    }

    findGiftMismatch(cart, unlocked) {
      const giftItems = this.getGiftItems(cart);
      const desiredId = Number(this.dataset.id);
      const desiredItem = giftItems.find((item) => Number(item.variant_id || item.id) === desiredId);
      const staleItem = giftItems.find((item) => Number(item.variant_id || item.id) !== desiredId);
      if (!unlocked && giftItems[0]) return { action: 'remove', cartItem: giftItems[0] };
      if (unlocked && !desiredItem) return { action: 'add' };
      if (unlocked && desiredItem && Number(desiredItem.quantity || 0) !== 1) {
        return { action: 'change', cartItem: desiredItem, quantity: 1 };
      }
      if (unlocked && staleItem) return { action: 'remove', cartItem: staleItem };
      return null;
    }

    async reconcileGift(initialCart = null) {
      if (!this.dataset.giftKey) return { changed: false, cart: initialCart };
      let state = initialCart?.items ? initialCart : await this.fetchCart();
      const giftItems = this.getGiftItems(state);
      const primaryItems = (state.items || []).filter((item) => !(
        itemProperty(item, '_theme_upsell_key')
        || itemProperty(item, '_theme_gift_key')
        || itemProperty(item, '_theme_additional_key')
      ));
      const basis = this.dataset.type === 'product_quantity'
        ? primaryItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
        : primaryItems.reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);
      const unlocked = primaryItems.length > 0
        && this.dataset.variantAvailable !== 'false'
        && basis >= Number(this.dataset.requirement || 0);
      this.setUnlocked(unlocked);

      if (window.Shopify?.designMode) return { changed: false, cart: state };
      let mismatch = this.findGiftMismatch(state, unlocked);
      let changed = false;
      let attemptsRemaining = 6;
      while (mismatch && attemptsRemaining > 0) {
        const result = await this.applyGiftMismatch(mismatch);
        state = mismatch.action !== 'add' && isFullCart(result) ? result : await this.fetchCart();
        changed = true;
        attemptsRemaining -= 1;
        mismatch = this.findGiftMismatch(state, unlocked);
      }
      if (mismatch) throw new Error('Gift cart state could not be synchronized.');
      return { changed, cart: state };
    }

    async fetchCart() {
      const response = await fetchCartResponse(`${window.routes.cart_url.replace(/\/$/, '')}.js`, {
        headers: { Accept: 'application/json' },
      });
      const state = await parseJsonResponse(response, 'Cart could not be loaded.');
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Cart could not be loaded.');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && isFullCart(state)) {
        drawer.lastKnownCart = state;
        drawer.cartStateStale = false;
      }
      return state;
    }

    async applyGiftMismatch(mismatch) {
      this.dataset.loading = 'true';
      try {
        const body = mismatch.action === 'add'
          ? {
              items: [{
                id: Number(this.dataset.id),
                quantity: 1,
                properties: { _theme_gift_key: String(this.dataset.giftKey || '') },
              }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }
          : {
              id: mismatch.cartItem?.key,
              quantity: mismatch.action === 'change' ? Number(mismatch.quantity || 1) : 0,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            };
        if (mismatch.action !== 'add' && !body.id) throw new Error('Gift cart item could not be identified.');
        const response = await postJson(
          mismatch.action === 'add' ? window.routes.cart_add_url : window.routes.cart_change_url,
          body
        );
        const state = await parseJsonResponse(response, 'Gift cart update failed.');
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Gift cart update failed.');
        document.querySelector('cart-drawer')?.renderContents(state, { reconcile: false });
        return state;
      } finally {
        this.dataset.loading = 'false';
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }
  }
  define('cart-drawer-gift', CartDrawerGift);

  class PromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.abortController = new AbortController();
      this.overlay = this.querySelector('.sign-up-popup-overlay');
      this.modal = this.querySelector('.sign-up-popup-modal');
      this.sectionRoot = this.closest('.shopify-section') || this.parentElement;
      this.successOverlay = this.sectionRoot?.querySelector('.success-popup-overlay');
      this.successModal = this.sectionRoot?.querySelector('.success-popup-modal');
      this.storageKey = `promo-popup-dismissed-${window.location.host}`;
      this.mode = this.dataset.mode || (this.dataset.testMode === 'true' ? 'testing' : 'enabled');
      this.isTestMode = this.mode === 'testing';

      const signal = this.abortController.signal;
      this.querySelectorAll('.promp-popup__close-btn').forEach((button) => {
        button.addEventListener('click', () => this.close({ remember: !this.isTestMode }), { signal });
      });
      this.successOverlay?.addEventListener('click', () => this.closeSuccess(), { signal });
      this.successModal?.querySelectorAll('.popup-modal__close, .popup-modal__dismiss-btn').forEach((button) => {
        button.addEventListener('click', () => this.closeSuccess(), { signal });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.successModal?.classList.contains('popup-modal--active')) this.closeSuccess();
        else if (this.isOpen()) this.close({ remember: !this.isTestMode });
      }, { signal });

      if (this.mode === 'success_testing') {
        document.body.classList.add('overflow-hidden');
      } else if (this.isTestMode) {
        this.open();
      } else if (!this.wasRecentlyDismissed()) {
        this.openTimer = window.setTimeout(
          () => this.open(),
          Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000
        );
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.countdownTimer);
      this.abortController?.abort();
      document.body.classList.remove('overflow-hidden');
      delete this.dataset.runtimeBound;
    }

    wasRecentlyDismissed() {
      try {
        const value = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        const dismissedAt = Number(value?.dismissedAt || 0);
        const delayDays = Math.max(0, Number(this.dataset.delayDays) || 0);
        return dismissedAt > 0 && Date.now() - dismissedAt < delayDays * 86400000;
      } catch (_error) {
        return false;
      }
    }

    rememberDismissal() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify({ dismissedAt: Date.now() }));
      } catch (_error) {
        // Storage can be unavailable in privacy-restricted browsers; the popup remains usable.
      }
    }

    isOpen() {
      return this.modal?.classList.contains('popup-modal--active');
    }

    open() {
      if (!this.modal || !this.overlay || this.isOpen()) return;
      this.previouslyFocused = document.activeElement;
      this.modal.classList.add('popup-modal--active');
      this.overlay.classList.add('popup-overlay--active');
      document.body.classList.add('overflow-hidden');
      this.startCountdown();
      window.requestAnimationFrame(() => {
        this.modal.querySelector('input, button, a[href], [tabindex]:not([tabindex="-1"])')?.focus();
      });
      dispatch(this, 'promo-popup:open');
    }

    close({ remember = true } = {}) {
      window.clearTimeout(this.openTimer);
      window.clearInterval(this.countdownTimer);
      this.modal?.classList.remove('popup-modal--active');
      this.overlay?.classList.remove('popup-overlay--active');
      document.body.classList.remove('overflow-hidden');
      if (remember) this.rememberDismissal();
      if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
      dispatch(this, 'promo-popup:close');
    }

    closeSuccess() {
      this.successModal?.classList.remove('popup-modal--active');
      this.successOverlay?.classList.remove('popup-overlay--active');
      document.body.classList.remove('overflow-hidden');
      dispatch(this, 'promo-popup:success-close');
    }

    startCountdown() {
      if (this.dataset.displayTimer !== 'true') return;
      const minutesNode = this.querySelector('.popup-modal__timer__minutes');
      const secondsNode = this.querySelector('.popup-modal__timer__seconds');
      if (!minutesNode || !secondsNode) return;

      window.clearInterval(this.countdownTimer);
      let remaining = Math.max(0, Math.round((Number(this.dataset.timerDuration) || 0) * 60));
      const render = () => {
        minutesNode.textContent = String(Math.floor(remaining / 60)).padStart(2, '0');
        secondsNode.textContent = String(remaining % 60).padStart(2, '0');
      };
      render();
      this.countdownTimer = window.setInterval(() => {
        remaining = Math.max(0, remaining - 1);
        render();
        if (remaining === 0) window.clearInterval(this.countdownTimer);
      }, 1000);
    }
  }
  define('promo-popup', PromoPopup);

  class SectionGroup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.runtimeBound) return;
      this.dataset.runtimeBound = 'true';
      this.abortController = new AbortController();
      this.moves = [];
      this.refresh = this.refresh.bind(this);

      const signal = this.abortController.signal;
      document.addEventListener('shopify:section:load', () => this.queueRefresh(), { signal });
      document.addEventListener('shopify:section:reorder', () => this.queueRefresh(), { signal });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.refresh, { once: true, signal });
      } else {
        this.queueRefresh();
      }
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.refreshFrame);
      this.abortController?.abort();
      this.restoreSections();
      delete this.dataset.runtimeBound;
    }

    queueRefresh() {
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(this.refresh);
    }

    resolveSection(selector) {
      const value = String(selector || '').trim();
      if (!value) return null;
      try {
        const normalized = value.startsWith('#') || value.startsWith('.') ? value : `#${CSS.escape(value)}`;
        return document.querySelector(normalized);
      } catch (_error) {
        return null;
      }
    }

    refresh() {
      this.restoreSections();
      const containers = [
        this.querySelector('.section-group__section-one-container'),
        this.querySelector('.section-group__section-two-container'),
      ];
      const sections = [
        this.resolveSection(this.dataset.sectionOneId),
        this.resolveSection(this.dataset.sectionTwoId),
      ];

      sections.forEach((section, index) => {
        const container = containers[index];
        if (!section || !container || section === this.closest('.shopify-section') || this.contains(section)) return;
        const placeholder = document.createComment(`section-group-placeholder-${index + 1}`);
        section.parentNode?.insertBefore(placeholder, section);
        container.append(section);
        section.dataset.sectionGroupManaged = 'true';
        this.moves.push({ section, placeholder });
      });

      this.classList.toggle('section-group--incomplete', this.moves.length !== 2);
      dispatch(this, 'section-group:ready', { sectionCount: this.moves.length });
    }

    restoreSections() {
      this.moves.splice(0).forEach(({ section, placeholder }) => {
        if (placeholder?.parentNode) placeholder.parentNode.insertBefore(section, placeholder);
        placeholder?.remove();
        delete section.dataset.sectionGroupManaged;
      });
    }
  }
  define('section-group', SectionGroup);
})();
